import {
  CHANNEL_CHECK_UNAVAILABLE_MESSAGE,
  SEAT_MOVED_MESSAGE,
  isolate,
  rtlLine,
  type ApiErrorCode,
  type LeaderboardPeriod,
  type ReportReason,
} from "@bing/shared";
import { faDigits } from "./digits";

/** Persian UI strings. The app is RTL/Persian-first; a full i18n layer lands later. */
export const fa = {
  maintenance: {
    /** The heading over the admin's own maintenance message. */
    title: "به‌روزرسانی",
    /**
     * The maintenance screen's door back to a LIVE table — this device's saved ticket (the same one
     * the lobby's «ادامه بازی» strip offers). Maintenance stops NEW games only; a seat the player
     * already holds is theirs to return to.
     */
    returnToGame: "بازگشت به بازی",
    /** …the same door when the ticket is a PRIVATE waiting room that has not dealt yet. */
    returnToTable: "بازگشت به میز",
    /**
     * PRE-DEPLOY WARNING — the app-wide apology modal an admin fires before a deploy (which will
     * close online tables). A heads-up only; it does NOT disconnect anyone.
     */
    warning: {
      title: "🔧 به‌زودی به‌روزرسانی!",
      body: "برای اینکه تجربهٔ بهتری داشته باشی، در حال بهبود بازی هستیم. تا چند دقیقهٔ دیگر بازی‌های آنلاینِ در جریان بسته می‌شن و میزِ فعلیت از دست می‌ره — بابت این وقفهٔ کوتاه عذرخواهی می‌کنیم 🙏",
      /** The single acknowledge button that dismisses the modal (client-side only). */
      dismiss: "متوجه شدم",
      /** Optional line appended when the push carries an `etaMinutes` hint. */
      eta: (minutes: string): string => `تقریباً ${minutes} دقیقهٔ دیگر`,
    },
  },
  channelGate: {
    /** The heading over the mandatory-channel join gate. */
    title: "عضویت در کانال‌ها",
    /** The «I joined, re-check» button. */
    joined: "عضو شدم",
    /** While the membership re-check is in flight. */
    checking: "در حال بررسی…",
    /** The membership came back APPROVED — the beat that carries them into the lobby. */
    approvedTitle: "عضویتت تأیید شد",
    approvedBody: "به بینگ گیمز خوش اومدی! 🎉",
    approvedCta: "بزن بریم!",
    /** Back to the lobby from a gate a TABLE join raised (the game-server's channel refusal). */
    back: "بازگشت",
    /** Ask again — the table join the game-server refused because Telegram did not answer in time. */
    retry: "تلاش دوباره",
  },
  appName: "بینگ گیمز",
  nav: {
    games: "بازی‌ها",
    friends: "دوستان",
    chat: "گفتگو",
    profile: "پروفایل",
    /* ⚠️ SHORT ON PURPOSE. The bar is `justify-around` inside a `px-gutter` box, and at 320px the
       four original items already occupy 217.5px of ~288px of content width (measured on the
       deployed build, 2026-09-10). «برترین‌ها» is the screen's own title; the tab gets the shorter
       «برترین», re-measured at 320 whenever this changes. */
    leaderboard: "برترین‌ها",
  },
  leaderboard: {
    title: "برترین‌ها",
    /* The three period tabs. «همیشه» rather than a date: the window genuinely grows on its own,
       because `MatchHistory.mode` only reaches back as far as the analytics retention allowed. */
    week: "هفته",
    month: "ماه",
    all: "همیشه",
    allGames: "همه",
    /** The single number every row shows. */
    wins: "برد",
    /** The pinned row's own label. */
    you: "تو",
    /** «۳ برد تا نفر بالاتر» — the gap to the rank above. */
    toNextRank: (n: string) => `${n} برد تا نفر بالاتر`,
    /**
     * Shown on the pinned row when the viewer has no online win in the SELECTED period.
     *
     * ⚠️ **IT MUST NAME THE PERIOD.** This was one sentence — «هنوز بردی در این بازه نداری» — and
     * «این بازه» names nothing: the period chips sit at the top of a page the podium can push out
     * of view, so a player with four wins read «you have no wins» and reported the board as broken.
     * It was telling the truth (their last online win was 23 h before the week bucket opened) and
     * the sentence gave them no way to know that. Measured 2026-09-11 on staging: both real
     * accounts read 0 over «هفته» and 4 and 3 over «ماه» / «همیشه».
     *
     * ⚠️ And «همیشه» says «برد آنلاینی» on purpose — over an unbounded window the only reason left
     * for a zero is that the board counts ONLINE wins and theirs were against bots.
     */
    unranked: {
      week: "این هفته بردی نداشتی",
      month: "این ماه بردی نداشتی",
      all: "هنوز برد آنلاینی نداری",
    } as Record<LeaderboardPeriod, string>,
    /**
     * THE CROSS-PERIOD HINT — the second half of the sentence above, naming a WIDER period the
     * viewer does have wins in.
     *
     * «این هفته بردی نداشتی» on its own is a dead end: it is the truth, and it reads as «you are
     * nobody here». «این هفته بردی نداشتی، ولی در یک ماه گذشته ۴ برد داری» is the same truth with
     * somewhere to go — the wins exist, they are just outside the window the player happens to be
     * looking through.
     *
     * ⚠️ Keyed on the TARGET period, and the Record is exhaustive so a fourth period cannot be
     * added without one. `week` is unreachable under today's week→month→all ladder (nothing is
     * narrower than «هفته» to be redirected FROM), and it is written out anyway rather than left
     * to a cast that would hide the next one.
     */
    butElsewhere: {
      week: (n: string) => `ولی این هفته ${n} برد داری`,
      month: (n: string) => `ولی در یک ماه گذشته ${n} برد داری`,
      all: (n: string) => `ولی در مجموع ${n} برد آنلاین داری`,
    } as Record<LeaderboardPeriod, (n: string) => string>,
    /**
     * The viewer is at rank 1 — there is no rank above to chase.
     *
     * ⚠️ A `hiddenFromBoard` sibling («آمار تو مخفی است…») was DELETED on 2026-09-14 with the
     * `hideStats` exclusion it explained. Everyone who can see this screen is on the board, so a
     * caller with no rank above them is simply the leader.
     */
    topOfBoard: "بالاترین رتبه",
    /**
     * The whole board is empty for this period + game filter.
     *
     * ⚠️ **IT NAMES THE PERIOD FOR THE SAME REASON `unranked` DOES, AND IT IS THE SAME SENTENCE
     * ONE STEP OUT.** This read «هنوز کسی در این بازه بردی ثبت نکرده» — and «این بازه» names
     * nothing, while the period chips sit at the top of a page the podium can push out of view.
     * That exact vagueness had already been reported once, on the pinned row, by a player with
     * four wins who read «you have no wins» and called the board broken. On «همیشه» it was worse
     * than vague: the window has NO lower bound at all, so «این بازه» asserted a period that does
     * not exist.
     *
     * ⚠️ And «همیشه» says «برد آنلاینی» on purpose, exactly as `unranked.all` does — over an
     * unbounded window the only reason left for an empty board is that it counts ONLINE wins and
     * this game's have been against bots.
     */
    empty: {
      week: "این هفته هنوز کسی بردی ثبت نکرده",
      month: "این ماه هنوز کسی بردی ثبت نکرده",
      all: "هنوز هیچ برد آنلاینی ثبت نشده",
    } as Record<LeaderboardPeriod, string>,
    /**
     * THE RULES CAPTION under the board — the operator's two sentences, verbatim (2026-09-27), one
     * line each. A profile's win total now differs from its board number for two reasons: solo
     * wins were never counted, and since the anti-farming guards a win can be refused (a 4th over
     * the same person in one Tehran day, or a private table with a bot). Nothing else says so.
     */
    rules: [
      "فقط بردهای آنلاین شمرده می‌شوند.",
      "بردهای تکراری مقابل یک حریف، روزانه محدود است.",
    ] as const,
    loadFailed: "جدول بارگذاری نشد",
  },
  // Shared, cross-screen labels.
  common: {
    back: "بازگشت",
  },
  lobby: {
    title: "بازی‌ها",
    // The activation badge on a COMING_SOON tile — matches the server's
    // GAME_COMING_SOON_MESSAGE wording so the badge and the gate speak alike.
    comingSoon: "بزودی",
    players: "بازیکن",
    /** Suffix of the live per-game online-count chip («۳ نفر آنلاین»). */
    onlineNow: "نفر آنلاین",
    // "Continue playing" strip (in-progress games)
    inProgress: "بازی‌های در جریان",
    resume: "ادامه",
    /** A PRIVATE table the player is seated at that has NOT dealt yet — they closed the app in
     *  their own waiting room. It rides the same strip, under a heading that says «در جریان», so
     *  the card itself has to be plain that no game has started. */
    tableNotStarted: "میز خصوصی · هنوز شروع نشده",
    profileAria: "پروفایل من",
  },
  // Lobby drawer destination: the store (فروشگاه) — empty until a catalogue lands.
  store: {
    title: "فروشگاه",
    empty: "فروشگاه فعلاً خالیه",
    hint: "به‌زودی چیزهای تازه اینجا می‌ذاریم",
  },
  // Lobby drawer destination: settings (تنظیمات) — sound, notifications, gameplay, display, privacy.
  settings: {
    title: "تنظیمات",
    audioSection: "صدا",
    gameplaySection: "بازی",
    sound: "صدای بازی",
    soundHint: "افکت‌های صوتی بازی",
    uiSound: "صدای پیام‌ها و اعلان‌ها",
    uiSoundHint: "پیام چت، درخواست دوستی و خبرها",
    volume: "بلندی صدا",
    // Notifications (the toggle moved here from Profile).
    notificationsSection: "اعلان‌ها",
    notifications: "اعلان‌های تلگرام",
    // ⚠️ NAMES EXACTLY WHAT THIS TOGGLE STILL GATES, AND ANNOUNCEMENTS ARE NOT IN IT. For one day
    // (2026-09-06) it also said «و خبرهای بینگ», because `recipientWhere` was filtering the
    // BROADCAST audience on `notificationsEnabled` too. That gating is GONE — a broadcast reaches
    // everyone now (operator's decision, same day) — so the clause would be a false promise the
    // other way round, describing an opt-out the player no longer has. Do not put it back without
    // first checking `recipientWhere` in @bing/db.
    notificationsHint: "برای درخواست دوستی و دعوت به بازی خبرت کنیم",
    // «بازی» group — lift a card, then tap the table to play it (حکم + بی‌دل). OFF by default.
    liftToPlay: "تأیید قبل از بازی کارت",
    liftToPlayHint: "کارت اول بالا میاد، با زدن روی میز بازی می‌شه",
    displaySection: "نمایش",
    theme: "پوسته",
    themeHint: "خودکار از پوستهٔ تلگرام پیروی می‌کنه",
    themeAuto: "خودکار",
    themeDark: "تیره",
    themeLight: "روشن",
    // Card deck («طرح کارت‌ها») — the player's own card-back skin for solo games.
    // Privacy — hide sections of your profile from OTHER users.
    privacySection: "حریم خصوصی",
    hideHistory: "پنهان‌کردن تاریخچهٔ بازی‌ها",
    hideHistoryHint: "دیگران تاریخچهٔ بازی‌هایت را نمی‌بینند",
    hideStats: "پنهان‌کردن آمار و افتخارات",
    hideStatsHint: "دیگران آمار و افتخاراتت را نمی‌بینند",
    /** The «کاربران مسدود» row — ALWAYS shown, count or no count. */
    blocked: "کاربران مسدود",
    blockedHint: "کسانی که مسدود کرده‌ای",
  },
  /** «کاربران مسدود» — the blocked list reached from تنظیمات. */
  blockedUsers: {
    title: "کاربران مسدود",
    /** Nobody blocked: said plainly, as an empty list should. */
    empty: "هنوز کسی را مسدود نکرده‌ای",
    emptyHint: "هر کسی را که مسدود کنی اینجا می‌بینی و می‌توانی آزادش کنی.",
    /** What a block actually does, in the words the confirm dialog uses. */
    intro: "این افراد نمی‌توانند به تو پیام بدهند یا دعوتت کنند.",
    unblock: "آنبلاک",
    unblockConfirmTitle: "آنبلاک",
    unblockConfirmBody: (name: string): string =>
      `${name} آنبلاک بشه؟ دوباره می‌تونید به هم پیام بدید — ولی به دوست‌هات برنمی‌گرده.`,
    unblockConfirm: "آره، آنبلاک کن",
    unblockCancel: "نه",
  },
  // AI opponent strength for solo games (and a host's bot-fill on private tables).
  difficulty: {
    // EVERY solo game now names its own level on its own start screen, so the wording carries no
    // scope any more: it used to say «بازی‌های کارتی» because one Settings row governed all five
    // card games at once, and that row is gone — nobody found it there.
    labelMain: "دشواری حریف",
    easy: "آسان",
    medium: "متوسط",
    hard: "سخت",
  },
  // The two questions EVERY solo card game asks before it deals — see `SoloStartScreen`.
  soloStart: {
    heading: "قبل از شروع",
    startGame: "شروع بازی",
  },
  // Lobby drawer destination: the game guides (راهنما).
  guides: {
    title: "راهنمای بازی‌ها",
    intro: "برای دیدن قوانین کامل هر بازی، روش بزن.",
  },
  // Lobby drawer destination: match history + stats (تاریخچه).
  history: {
    title: "تاریخچه بازی‌ها",
  },
  // In-app inbox — «@bing» broadcasts (system messages, not bot DMs).
  messages: {
    title: "پیام‌ها",
    empty: "هنوز پیامی نداری",
    emptyHint: "خبرها و اطلاعیه‌های بینگ گیمز اینجا نمایش داده می‌شن",
    loading: "در حال بارگذاری…",
    error: "پیام‌ها بارگذاری نشد",
    retry: "تلاش دوباره",
    deleteOne: "حذف پیام",
    count: "پیام",
    clearAll: "پاک کردن همه",
    clearAllTitle: "پاک کردن همهٔ پیام‌ها",
    clearAllBody: "همهٔ پیام‌ها برای همیشه حذف می‌شن. مطمئنی؟",
    clearAllConfirm: "آره، پاک کن",
    clearAllCancel: "بی‌خیال",
  },
  // Reporting another user (profile view / friends list).
  report: {
    action: "گزارش",
    title: (name: string) => `گزارش ${name}`,
    prompt: "دلیل گزارش را انتخاب کن:",
    reasons: {
      SPAM: "هرزنامه",
      ABUSE: "توهین یا آزار",
      CHEATING: "تقلب",
      INAPPROPRIATE: "محتوای نامناسب",
      OTHER: "سایر",
    } as Record<ReportReason, string>,
    detailsPlaceholder: "توضیح بیشتر (اختیاری)…",
    submit: "ثبت گزارش",
    submitting: "در حال ثبت…",
    done: "گزارش شما ثبت شد. ممنون که کمک کردی؛ تیم ما بررسی می‌کند.",
    error: "ثبت گزارش انجام نشد",
    // Evidence — mandatory. Every failure here gets a sentence the user can act on; a report
    // that cannot be filed must never be a dead screen.
    // Required only where one frame proves something (توهین / محتوای نامناسب); تقلب and هرزنامه
    // are patterns a single screenshot cannot show, so evidence there is welcome but not demanded.
    attachTitleRequired: "عکس یا فیلم صفحه (الزامی)",
    attachTitleOptional: "عکس یا فیلم صفحه (اختیاری)",
    // ⚠️ THE LIMIT IS NAMED UP FRONT. It used to appear only once you were already full, so the
    // rule was invisible right up to the moment it bit (reported on device, 2026-08-08).
    attachHint:
      "از صفحه‌ای که مشکل را نشان می‌دهد عکس بگیر، بعد همین‌جا ضمیمه‌اش کن. حداکثر ۳ فایل، هرکدام تا ۵ مگابایت.",
    attachHintOptional:
      "اگر عکس یا فیلمی داری ضمیمه کن؛ برای این دلیل الزامی نیست. حداکثر ۳ فایل، هرکدام تا ۵ مگابایت.",
    attachPick: "انتخاب عکس یا فیلم",
    attachAdd: "افزودن فایل دیگر",
    attachChange: "تغییر فایل",
    /** Both already in Persian digits — the sheet owns the numeral formatting. */
    attachCount: (n: string, max: string) => `${n} از ${max}`,
    attachRemove: "حذف فایل",
    attachMaxReached: "حداکثر سه فایل می‌تونی ضمیمه کنی.",
    /** Fired the instant a pick exceeds the cap — before any re-encode or upload runs. `skipped`
     *  arrives already in Persian digits (the sheet owns the numeral formatting). */
    attachTooMany: (skipped: string) =>
      `حداکثر ۳ فایل می‌تونی ضمیمه کنی؛ ${skipped} مورد اضافه نشد.`,
    attachPreparing: "در حال آماده‌سازی…",
    attachUploading: "در حال بارگذاری…",
    attachReady: "ضمیمه شد",
    attachRequired: "برای ثبت گزارش باید عکس یا فیلم صفحه را ضمیمه کنی.",
    attachTooLarge: "حجم فایل بیشتر از ۵ مگابایت است؛ فایل کوچک‌تری انتخاب کن.",
    attachUnsupported: "این نوع فایل پشتیبانی نمی‌شود؛ عکس یا فیلم انتخاب کن.",
    attachFailed: "بارگذاری کامل نشد؛ دوباره تلاش کن.",
    /** A parked draft can outlive the server's hold on its upload — re-pick, not a dead submit. */
    attachExpired: "این فایل خیلی وقت پیش فرستاده شده و دیگه معتبر نیست؛ دوباره انتخابش کن.",
    attachRetry: "تلاش دوباره",
    // ⚠️ A REPORT MUST NEVER BE A TRAP (2026-10-03): a player learned only INSIDE this sheet that
    // توهین needs a screenshot, found no way back, and left the table. The way back is always
    // there, the requirement is said before the reasons, and nothing chosen is lost on the way.
    back: "بازگشت",
    /** `reasons` arrives already joined — the sheet derives it from the shared required list. */
    shotNotice: (reasons: string) => `برای ${reasons} اسکرین‌شات (عکس صفحه) لازمه.`,
    shotNoticeTable:
      "اگه هنوز نگرفتی، برگرد به میز و اسکرین‌شات بگیر؛ چیزهایی که اینجا انتخاب کردی سر جاش می‌مونه.",
    shotDoor: "برگشت به میز برای اسکرین‌شات",
    /** The same door on a table WITH chat — it lands on the chat, where the evidence is. */
    shotDoorChat: "برگشت به گفتگوی میز برای اسکرین‌شات",
    yourTurn: "نوبت توئه! برگرد سر میز و بازی کن؛ گزارشت همین‌جا می‌مونه.",
    yourTurnAction: "برگشت به میز",
    // «دربارهٔ»: «گزارش رضا» alone reads as Reza's OWN report as readily as one about him.
    resumeLabel: (name: string) => `گزارشت دربارهٔ ${name} نیمه‌کاره مونده`,
    resume: "ادامهٔ گزارش",
    discard: "لغو گزارش",
  },
  // Viewing ANOTHER user's profile (name/handle always; stats + history if not hidden).
  profileView: {
    title: "پروفایل",
    history: "تاریخچهٔ بازی‌ها",
    stats: "آمار و افتخارات",
    private: "این بخش خصوصی است",
    noMatches: "هنوز بازی‌ای انجام نشده",
    loading: "در حال بارگذاری…",
    loadError: "پروفایل بارگذاری نشد",
    retry: "تلاش دوباره",
  },
  friends: {
    title: "دوستان",
    // Sub-tabs
    tabFriends: "دوستان",
    tabRequests: "درخواست‌ها",
    tabAdd: "افزودن",
    // Search / Add
    searchLabel: "جستجوی کاربران",
    searchPlaceholder: "نام کاربری یا شناسه…",
    searchHint: "حداقل ۴ کاراکتر وارد کنید",
    searching: "در حال جستجو…",
    noResults: "کاربری پیدا نشد",
    // Add-tab per-row actions (by relationship status)
    add: "افزودن دوست",
    requested: "در انتظار پاسخ",
    alreadyFriends: "دوست شما",
    unblock: "آنبلاک",
    // Requests tab
    requestsTitle: "درخواست‌های دوستی",
    incomingTitle: "دریافتی",
    outgoingTitle: "ارسالی",
    accept: "پذیرش",
    reject: "رد",
    cancel: "لغو درخواست",
    noIncoming: "درخواست دوستی جدیدی ندارید",
    noOutgoing: "درخواست ارسالی ندارید",
    // Friends tab
    friendsTitle: "دوستان شما",
    invite: "دعوت به بازی",
    remove: "حذف",
    // Remove-friend confirmation (friendly-casual, like the exit dialog).
    removeConfirmTitle: "حذف دوست",
    removeConfirmBody: (name: string): string => `${name} از دوستات حذف بشه؟`,
    removeConfirm: "آره، حذف کن",
    removeCancel: "نه",
    online: "آنلاین",
    offline: "آفلاین",
    inGame: "در حال بازی",
    // Live friend-event toast (mirrors the bot notification text).
    pushRequest: "برایت درخواست دوستی فرستاد",
    pushAccept: "درخواست دوستی را پذیرفت!",
    noFriends: "هنوز دوستی ندارید",
    noFriendsHint: "از بخش «افزودن» دوستان خود را پیدا کنید",
    // Shared list states
    loading: "در حال بارگذاری…",
    loadError: "خطا در بارگذاری دوستان",
    retry: "تلاش مجدد",
    actionError: "انجام نشد؛ دوباره تلاش کنید",
    count: "نفر",
    // Last-resort label when a user has NO nickname, Telegram name, or handle.
    user: "(کاربر)",
  },
  // Game invites (friend → private table). TTL is 60s (GAME_INVITE_TTL_MS).
  invite: {
    // Send flow (friend row → game sheet → host room → auto-invite)
    sheetTitle: (name: string): string => `دعوت ${name} به بازی`,
    pickGame: "بازی رو انتخاب کن؛ میز خصوصی ساخته می‌شه و دعوت براش می‌ره",
    inviteFriendsTitle: "دعوت از دوستان",
    invite: "دعوت",
    reinvite: "دعوت دوباره",
    cancel: "لغو دعوت",
    // Outgoing invite states (host's waiting-room panel)
    pending: "در انتظار پاسخ",
    accepted: "پیوست",
    declined: "رد کرد",
    expired: "منقضی شد",
    canceled: "لغو شد",
    seconds: "ثانیه",
    // Incoming prompt
    incoming: (name: string, game: string): string => `${name} تو را به بازی ${game} دعوت کرد`,
    accept: "پیوستن",
    decline: "رد",
    expiredNotice: "این دعوت منقضی شد",
    // Tapping a game-invite toast that we cannot turn into a prompt even after a refetch.
    // Deliberately NON-committal: it may have expired, been withdrawn, or the fetch may simply
    // have failed — «منقضی شد» would assert a cause we have not established. Saying nothing at
    // all is the one option ruled out: that is the dead-end notice this whole change removes.
    unavailable: "این دعوت دیگر در دسترس نیست",
    // Join-after-accept failures (Colyseus join-by-id, code 4212)
    // The generic pair — all Colyseus itself can tell us («not found» / «is locked»).
    // Deliberately NON-committal now: the old «میز پر است یا بازی شروع شده است» asserted
    // two things that are both false for the commonest case — the game is simply over.
    roomGone: "این میز دیگر در دسترس نیست",
    /** The reconnect TICKET expired, not the table — «reconnection token invalid or expired.»
     *  from `client.reconnect`. The room may still be alive, so this must not say it is gone. */
    seatExpired: "فرصت برگشتن به این میز تمام شد",
    roomLocked: "دیگر نمی‌توانی وارد این میز شوی",
    // …and the TRUE reasons, from what the table itself recorded (`GET /tables/:id/status`).
    gameFinished: "این بازی تمام شده است",
    tableClosed: "میزبان این میز را بست",
    tableExpired: "این میز منقضی شد",
    gameStarted: "بازی این میز شروع شده است",
    roomFull: "میز پر است",
    /*
     * …AND HOW IT ENDED, for the player coming BACK to a table that is over (session B, item 3,
     * 2026-10-08) — one line under «این بازی تمام شده است», from the `end` the status route now
     * carries (`tableEndLine`). The reader's own result and facts about the table; never what the
     * end did to anybody else's record (`away-copy-rule.test.ts`). An unknown reason, or no `end`
     * (an old api), shows the first line alone.
     */
    endedClaimLoss: "بعد از رفتنت، حریفت برد رو ادعا کرد و باخت به نامت ثبت شد",
    endedClaimDraw: "بعد از رفتنت، حریفت بازی رو تموم کرد و نتیجه مساوی ثبت شد",
    endedResignLoss: "از این بازی انصراف دادی و باخت به نامت ثبت شد",
    endedTimeoutWin: "وقت حریفت تموم شد و برد به نامت ثبت شد",
    endedTimeoutLoss: "وقتت تموم شد و باخت به نامت ثبت شد",
    endedTimeoutDraw: "وقت تموم شد و نتیجه مساوی ثبت شد",
    endedIdleLoss: "چند نوبت پشت‌سرهم بازی نکردی و باخت به نامت ثبت شد",
    endedWin: "برد این بازی به نامت ثبت شد",
    endedLoss: "باخت این بازی به نامت ثبت شد",
    endedDraw: "این بازی مساوی ثبت شد",
    // Live push toasts (invite kinds; the friend kinds live in fa.friends)
    pushInvite: (name: string, game: string): string =>
      game ? `${name} تو را به بازی ${game} دعوت کرد` : `${name} تو را به بازی دعوت کرد`,
    pushDeclined: (name: string): string => `${name} دعوتت را رد کرد`,
    // The friend sees this when the inviter cancels OR their game starts — the invite is no longer
    // valid (the counter/prompt clears live). Names WHO withdrew it, matching the retracted DM.
    pushCanceled: (name: string): string => `🚫 «${name}» دعوتش رو پس گرفت`,
    pushExpired: (name: string): string => `دعوتت به ${name} منقضی شد`,
    // Accepting an invite while ALREADY in a game — confirm, then an animated switch stage.
    switchTitle: "رفتن به بازی جدید؟",
    switchBodySolo: "بازی فعلی‌ات ذخیره می‌شه و بعداً می‌تونی ادامه‌اش بدی.",
    switchBodyOnline: "از بازی فعلی خارج می‌شی و جات به ربات سپرده می‌شه.",
    switchConfirm: "بریم به بازی جدید",
    switchCancel: "بی‌خیال",
    switchingSolo: "در حال ذخیرهٔ بازی فعلی و اتصال به بازی بعدی…",
    switchingOnline: "در حال خروج از بازی فعلی و اتصال به بازی بعدی…",
    switchingLobby: "در حال اتصال به بازی…",
    /**
     * SEATED IN A LIVE PUBLIC GAME ⇒ THE INVITE CANNOT BE ACCEPTED AT ALL (operator, 2026-08-27).
     *
     * Accepting used to leave the public table by the ordinary resumable path — no loss, seat
     * reclaimable, counter untouched — which was a second door out of a public game that cost
     * nothing. The fix is to REFUSE the accept, not to penalise it: nobody should be punished for
     * being invited. So the card still arrives and still says who invited them; it just says
     * plainly why «پیوستن» is not there, and leaves «رد» so the friend gets an answer instead of
     * waiting out the window.
     */
    blockedPublic: "الان توی یک بازی عمومی هستی و نمی‌تونی این دعوت رو قبول کنی.",
    blockedPublicWhy: "ترک کردن بازی عمومی، باخت ثبت می‌کنه.",
  },
  /**
   * Persian reasons for the api's stable error codes (`{code}` in every error
   * body). `faApiError` (i18n/errors.ts) maps a caught `ApiError` through this
   * table; unknown/absent codes fall back to the generic `friends.actionError`.
   */
  errors: {
    // ⚠️ IT MUST NOT PROMISE THE REOPEN. The api client heals a stale session with ONE guarded
    // reload (`reloadOnceForFreshInitData`), and the guard is in `sessionStorage`, which SURVIVES
    // that reload — so on a second expiry, or where storage is unavailable, nothing reopens and
    // the old wording («برنامه دوباره باز می‌شود») was a promise the app could not keep, on a
    // screen that then offered nothing to press. Phrased as the ACTION instead, it is true
    // whoever performs it: the heal is doing it, or the reload button under it is there to.
    AUTH_EXPIRED: "نشست منقضی شده — برنامه را دوباره باز کن",
    AUTH_INVALID: "خطای ورود؛ برنامه را از تلگرام باز کن",
    VALIDATION: "درخواست نامعتبر بود",
    USERNAME_RESERVED: "این نام کاربری رزرو شده و قابل استفاده نیست",
    FORBIDDEN: "اجازهٔ این کار را نداری",
    NOT_FOUND: "کاربر پیدا نشد",
    CONFLICT: "وضعیت تغییر کرده؛ صفحه را تازه کن",
    INTERNAL: "خطای سرور؛ کمی بعد دوباره تلاش کن",
    MAINTENANCE: "در حال به‌روزرسانی هستیم، به‌زودی برمی‌گردیم",
    BANNED: "حساب شما مسدود شده است.",
    CHANNELS_REQUIRED: "برای استفاده، اول در کانال‌های اجباری عضو شو.",
    SELF_ACTION: "این کار را روی خودت نمی‌توانی انجام دهی",
    ALREADY_FRIENDS: "شما از قبل دوست هستید",
    ALREADY_PENDING: "درخواست قبلاً ارسال شده",
    REVERSE_PENDING: "او هم برایت درخواست فرستاده؛ از بخش درخواست‌ها پاسخ بده",
    NO_REQUEST: "درخواستی برای پاسخ وجود ندارد",
    NOT_FRIENDS: "فقط دوستان می‌توانند این کار را انجام دهند",
    BLOCKED: "به دلیل مسدود بودن، این کار ممکن نیست",
    INVITE_EXPIRED: "این دعوت منقضی شده است",
    INVITE_NOT_PENDING: "این دعوت دیگر معتبر نیست",
    // The api's own body carries the same sentence; this is the fallback for a body without it.
    CHANNEL_CHECK_UNAVAILABLE: CHANNEL_CHECK_UNAVAILABLE_MESSAGE,
  } satisfies Record<ApiErrorCode, string>,
  chat: {
    title: "گفتگو",
    pickFriend: "برای شروع گفتگو یک دوست را انتخاب کنید",
    noFriends: "برای گفتگو ابتدا دوستانی اضافه کنید",
    tapToChat: "برای شروع گفتگو ضربه بزنید",
    /** Last-message preview label for a voice note (its body is empty on the wire). */
    voicePreview: "🎤 پیام صوتی",
    loadingOlder: "در حال بارگذاری پیام‌های قدیمی‌تر…",
    threadStart: "ابتدای گفتگو",
    newConversation: "گفتگوی جدید",
    noFriendsToChat: "هنوز دوستی نداری که باهاش گفتگو کنی",
    moreConversations: "گفتگوهای بیشتر",
    searchConversations: "جستجو",
    messageAction: "پیام",
    /** «شما:» prefix on a preview of the caller's own last message. */
    youPrefix: "شما",
    connecting: "در حال اتصال…",
    inputPlaceholder: "پیام بنویسید…",
    /** The 30-day retention notice (conversation list + thread). */
    retentionNotice: "گفتگوها فقط تا ۳۰ روز نگه‌داری می‌شوند و پس از آن خودکار حذف می‌شوند.",
    /** One-sided «حذف گفتگو» (chat-list row): hide the thread from MY list only. */
    deleteThread: "حذف گفتگو",
    deleteThreadBody: "این گفتگو فقط برای شما حذف می‌شود و برای طرف مقابل باقی می‌ماند.",
    deleteThreadConfirm: "آره، حذف کن",
    deleteThreadCancel: "بی‌خیال",
    send: "ارسال",
    empty: "هنوز پیامی نیست. سلام کنید!",
    error: "خطا در اتصال به گفتگو",
    /**
     * THE TABLE IS GONE — a TERMINAL refusal, so it is a statement rather than a failure, and it
     * comes with no «تلاش دوباره». Table-chat membership is registered by the game room and
     * cleared when it disposes, so `TABLE_CHAT_NOT_A_MEMBER` can only mean this; offering a retry
     * for it was a button that could never succeed. See `ChatState.canRetry`.
     */
    tableGone: "این میز دیگر وجود ندارد",
    /** Slim inline strip while the lifecycle re-establishes a dropped chat socket. */
    reconnecting: "در حال اتصال دوباره…",
    /** Manual restart of the reconnect cycle after its attempts were exhausted. */
    retry: "تلاش دوباره",
    back: "بازگشت",
    // Unread messages (chat-list row chips + the bottom-nav badge).
    unread: (count: string): string => `${count} پیام خوانده‌نشده`,
    // Live toast for a DM that landed while the thread wasn't open.
    pushDm: "برایت پیام فرستاد",
    // Emoji picker (composer)
    emoji: "شکلک‌ها",
    emojiClose: "بستن شکلک‌ها",
    emojiGroups: {
      faces: "صورتک‌ها",
      hearts: "قلب‌ها",
      hands: "دست‌ها",
      games: "بازی و سرگرمی",
      celebration: "جشن",
      animals: "حیوانات",
      food: "خوراکی",
    },
    // Blocking inside a DM thread (friendly-casual, like the exit dialog).
    block: "مسدود کردن",
    unblock: "آنبلاک",
    blockConfirmTitle: "مسدود کردن",
    blockConfirmBody: (name: string): string =>
      `${name} مسدود بشه؟ دیگه نمی‌تونید به هم پیام بدید و از دوستات حذف می‌شه.`,
    blockConfirm: "آره، مسدود کن",
    blockCancel: "نه",
    /** Compact composer notice while YOU have the peer blocked. */
    blockedNotice: "این کاربر رو مسدود کردی",
    /** The action beside it — this REPLACES the message box, so it has to read as the way out. */
    blockedUnblock: "رفع مسدودی",
    /**
     * The server refused a send because a block exists.
     *
     * ⚠️ ONLY A BLOCKER EVER SEES THIS NOW. It used to reach the blocked party too, and saying
     * «نمی‌تونی به این کاربر پیام بدی» to them announced the block in as many words; their send
     * succeeds and is swallowed. The old comment here read «typically the peer blocked you»,
     * which is now precisely the one case that cannot produce it.
     */
    blockedSendError: "نمی‌تونی به این کاربر پیام بدی",
    /** Muted glyph/hint on a chat-list row whose peer the user has blocked. */
    blockedPeer: "مسدود شده",
    // Voice notes
    recordVoice: "ضبط پیام صوتی",
    cancelVoice: "لغو",
    sendVoice: "ارسال پیام صوتی",
    sending: "در حال ارسال…",
    micError: "دسترسی به میکروفون ممکن نشد",
    // Branded pre-prompt shown BEFORE the native mic dialog on the first recording.
    micAskTitle: "اجازه‌ی میکروفون",
    micAskBody: "برای ضبط پیام صوتی به میکروفون نیاز داریم.",
    // After our button, the OS shows its OWN dialog — labeled in the phone's language.
    micAskHint: "بعدش یک پیام سیستمی می‌آد؛ روی Allow / «اجازه دادن» بزن.",
    micAskAllow: "اجازه دادن",
    micAskLater: "بعداً",
    voiceSendError: "ارسال پیام صوتی ناموفق بود",
    voiceLoadError: "خطا در بارگذاری صدا",
    playVoice: "پخش پیام صوتی",
    pauseVoice: "توقف پیام صوتی",
    voiceNote: "پیام صوتی",
    // Table chat panel (mounted over the online felt)
    tableChat: "گفتگوی میز",
    openChat: "گفتگو",
    closeChat: "بستن گفتگو",
    // Table sheet tabs (گفتگو | بازیکن‌ها) + the players panel rows
    chatTab: "گفتگو",
    playersTab: "بازیکن‌ها",
    /** Roster label for a bot-filled seat (players panel). */
    botPlayer: "بات",
    /** Marker on your own roster row. */
    youMarker: "(شما)",
    /** Disabled pill on an already-friend table-mate. */
    alreadyFriends: "دوستید",
    /* «اخراج» — the private host removing a seated player (2026-09-02). Host-only, on any
       OTHER human's roster row, and behind a confirmation because it cannot be undone. */
    kick: "اخراج",
    kickConfirmTitle: "اخراج از میز؟",
    kickConfirmBody: (name: string): string =>
      `«${name}» از این میز اخراج می‌شود و دیگر نمی‌تواند برگردد — نه با لینک دعوت، نه از «ادامه بازی». جای او را ربات می‌گیرد.`,
    kickConfirm: "بله، اخراجش کن",
    kickCancel: "بی‌خیال",
  },
  profile: {
    title: "پروفایل",
    nickname: "نام نمایشی",
    nicknamePlaceholder: "خالی بگذارید تا نام تلگرام استفاده شود",
    nicknameClear: "حذف نام نمایشی",
    nicknameTooLong: "نام نمایشی حداکثر ۶۴ نویسه می‌تواند باشد",
    unsavedTitle: "تغییرات ذخیره‌نشده",
    unsavedBody: "تغییراتت هنوز ذخیره نشده. می‌خواهی ذخیره کنی یا لغوشان کنی؟",
    unsavedSave: "ذخیره و خروج",
    unsavedDiscard: "لغو تغییرات",
    unsavedStay: "ماندن",
    avatar: "آواتار",
    save: "ذخیره",
    saving: "در حال ذخیره…",
    saved: "ذخیره شد",
    saveError: "ذخیره نشد؛ دوباره تلاش کنید",
    // Nav buttons below Save (not profile fields — they open their own screens).
    openSettings: "تنظیمات",
    openHistory: "تاریخچه",
    back: "بازگشت",
    sound: "صدای بازی",
    editAvatar: "ویرایش آواتار",
    // App username (Telegram-style handle)
    username: "نام کاربری",
    usernamePlaceholder: "یک نام کاربری انتخاب کنید",
    usernameHint: "۵ تا ۳۲ نویسه؛ حروف انگلیسی، اعداد و «_»؛ با یک حرف شروع شود.",
    usernameChecking: "در حال بررسی…",
    usernameAvailable: "این نام کاربری آزاد است",
    usernameTaken: "این نام کاربری قبلاً گرفته شده",
    usernameReserved: "این نام کاربری رزرو شده و قابل استفاده نیست",
    usernameInvalid: "نام کاربری نامعتبر است",
    // Avatar photo upload
    photoTitle: "عکس پروفایل",
    changePhoto: "تغییر عکس",
    camera: "دوربین",
    gallery: "گالری",
    file: "فایل",
    presets: "آواتار آماده",
    uploading: "در حال بارگذاری عکس…",
    uploadSuccess: "عکس پروفایل به‌روزرسانی شد",
    uploadError: "بارگذاری عکس ناموفق بود؛ اتصال را بررسی کنید",
    photoTooLarge: "حجم عکس باید کمتر از ۲ مگابایت باشد",
    photoUnsupported: "فرمت این عکس پشتیبانی نمی‌شود؛ یک عکس JPEG یا PNG انتخاب کنید",
    // Avatar crop sheet (frame the picked photo before it uploads)
    cropTitle: "برش عکس پروفایل",
    cropHint: "عکس را بکشید و جابه‌جا کنید؛ با دو انگشت یا اسلایدر بزرگ‌نمایی کنید",
    cropZoom: "بزرگ‌نمایی",
    cropSave: "ذخیره",
    cropCancel: "انصراف",
    // Shown when the picked image will not decode on THIS device (the crop sheet's <img> errors).
    // Names the device deliberately: the file is usually fine, it is this WebView that can't read it.
    cropLoadFailed: "این عکس روی دستگاه شما باز نشد؛ عکس دیگری انتخاب کنید",
    // Notifications
    notifications: "اعلان‌ها",
    // Stats
    stats: "آمار بازی‌ها",
    games: "بازی‌ها",
    wins: "بردها",
    losses: "باخت‌ها",
    draws: "مساوی",
    perGame: "به تفکیک بازی",
    noStats: "هنوز آماری ثبت نشده",
    // Match history
    history: "تاریخچه بازی‌ها",
    loadMore: "بیشتر",
    loadingHistory: "در حال بارگذاری…",
    noHistory: "هنوز بازی‌ای انجام نداده‌اید",
    historyError: "خطا در بارگذاری تاریخچه",
    resultWin: "برد",
    resultLoss: "باخت",
    resultDraw: "مساوی",
  },
  // Branded app-boot splash — staged captions (useBootSequence), in order.
  boot: {
    wordmark: "بینگ گیمز", // splash wordmark (matches the app brand fa.appName «بینگ گیمز»)
    preparing: "در حال آماده‌سازی…",
    connecting: "در حال اتصال شما به بازی…",
    fetching: "در حال دریافت اطلاعات…",
    almost: "تقریباً آماده‌ست…",
    slow: "کمی طول کشیده؛ لطفاً صبر کنید…",
    /**
     * Shown on the boot error screen when TELEGRAM LAUNCHED US AND WE COULD NOT READ THE LAUNCH —
     * `launchParamsUnusable`. The api's answer is identical to «opened outside Telegram» (401
     * `AUTH_INVALID`, because no header was sent at all), so without this the player is told to
     * open the app from Telegram while they are sitting in it.
     *
     * ⚠️ DELIBERATELY DOES NOT NAME A CAUSE. The known one — an Android System WebView below
     * Chrome 93 — is FIXED by `ensureSdkPolyfills()`, so anyone still reaching this line is
     * failing for a reason we have not measured, and naming the WebView would be the second
     * confident wrong diagnosis on the same screen.
     *
     * ⚠️ AND DELIBERATELY NO LATIN: a brand name («Play Store») would make this an emoji/Latin-led
     * line needing `rtlLine` + `isolate` to render in the right visual order (see the 🆕 entry in
     * CLAUDE.md `## Gotchas`). Pure Persian needs neither.
     */
    signInFailedOnDevice:
      "ورود به بازی روی این دستگاه ممکن نشد — تلگرام و برنامه‌های گوشی‌ات را به‌روزرسانی کن و دوباره امتحان کن",
  },
  // One-time first-entry welcome (shown once to brand-new users).
  welcome: {
    title: "🎴 به بینگ گیمز خوش اومدی!",
    // Two paragraphs — the overlay renders this `pre-line`, so the blank line survives. Names
    // BOTH sections now that the app is no longer card-games-only.
    body: "بازی‌های کارتی و تخته‌ای — از حکم و بی‌دل تا منچ و تخته‌نرد — آنلاین با دوستات یا حریف‌های تصادفی.\n\nیه اسم و عکس برای خودت بذار، دوست پیدا کن و شروع کن!",
    cta: "بزن بریم",
  },
  status: {
    loading: "در حال بارگذاری…",
    error: "خطا در بارگذاری. دوباره تلاش کنید.",
    // Shown on the boot error screen after the resilient getMe exhausts its retries on a NETWORK
    // failure — replaces the raw browser string («Load failed» / «Failed to fetch»).
    // ⚠️ IT MUST NOT PRESCRIBE A VPN. The previous wording («…لطفاً با VPN روشن امتحان کنید») told
    // people who were ALREADY on a proxy to turn one on, and they read it as «the game now blocks
    // proxies» — a real 2026-08-25 report. This screen only ever means «no answer yet»; it can
    // never mean «we refused you» (a server reason is Persian and is kept verbatim by
    // `bootErrorMessage`), so it must describe the link and offer the retry, nothing more.
    offline: "اتصال برقرار نشد — اینترنت شما کند یا ناپایدار است. دوباره تلاش کنید",
    /** Shown while a card table waits for its artwork chunk — so a longer-than-usual bar says
     *  WHAT it is waiting for instead of looking stuck. */
    preparingCards: "در حال آماده‌سازی کارت‌ها…",
    retry: "تلاش مجدد",
    /** The boot error screen's OTHER action: load the app afresh. Distinct from `retry`, which
     *  re-runs the same request with the same credentials — for a stale session only a full
     *  reload can mint new Telegram initData, so the two must never share a label. */
    reopen: "باز کردن دوباره",
    // The ErrorBoundary fallback — shown INSTEAD of the blank page a render crash used to leave.
    // Deliberately blames nothing and asks for the one action that actually helps (a fresh load).
    renderCrash: "مشکلی پیش اومد و صفحه درست باز نشد.\nیک‌بار دوباره امتحان کن.",
    /** A «ادامه بازی» of a save made under rules the game no longer plays: the server deleted it
     *  and refused the join with `SAVED_GAME_RETIRED_MESSAGE`, which `localizedReason` maps here.
     *  Says what happened and what to do — never a connection error, never a silent vanish. */
    savedGameRetired:
      "قانون‌های بازی به‌روز شده و این بازی ذخیره‌شده دیگر ادامه پیدا نمی‌کند — یک بازی تازه شروع کن",
  },
  // App-level connection overlay (screen-lock / backgrounding recovery).
  // Friendly-casual register (خودمونی), like the rest of the room copy.
  connection: {
    reconnecting: "داریم دوباره وصلت می‌کنیم…",
    // Soft reassurance under the spinner (replaces the old «تلاش n از ۶» counter).
    reconnectingBody: "یه لحظه صبر کن، الان برمی‌گردی به بازی.",
    attempt: (n: string, total: string): string => `تلاش ${n} از ${total}`,
    failedTitle: "اتصال برقرار نشد",
    failedBody: "اینترنتت رو چک کن و یه بار دیگه امتحان کن.",
    retry: "تلاش دوباره",
    exit: "خروج",
    seatExpiredTitle: "جات به ربات سپرده شد",
    seatExpiredBody: "اتصال خیلی طول کشید و بازی بدون تو ادامه پیدا کرد.",
    seatFreedTitle: "اتصال خیلی طول کشید",
    seatFreedBody: "بازی هنوز شروع نشده بود؛ جای تو در میز آزاد شد. می‌تونی یه میز تازه بسازی.",
    backToLobby: "برگرد به لابی",
  },
  /**
   * «Turn your phone upright» (card-f5 item 3) — covers the app while a PHONE shows it sideways
   * (opened sideways, or a Telegram that cannot lock the orientation). Pure Persian on purpose: no
   * Latin or emoji inside a line, so neither needs `rtlLine`/`isolate`. Makes no promise about a
   * game underneath — it keeps running, turn timer included.
   */
  upright: {
    title: "گوشی رو عمودی بگیر",
    body: "بازی‌ها برای حالت عمودی ساخته شدن؛ گوشی رو بچرخون تا ادامه بدی.",
  },
  // Shared exit-confirmation dialog (every in-game leave routes through it).
  // Friendly-casual register (خودمونی) — informal 2nd-person, like the lobby copy.
  confirm: {
    exitTitle: "خروج از بازی؟",
    exitBody: "از بازی می‌ری بیرون؟",
    exitConfirm: "آره، می‌رم",
    exitCancel: "می‌مونم",
    // Two-option offline-solo exit: keep the game resumable, OR end it for good.
    // Body maps each button; «خروج» keeps it, «پایان بازی» removes it.
    exitChoiceBody:
      "با «خروج» بازی ذخیره می‌شه و بعداً ادامه‌ش می‌دی. با «پایان بازی» بازی تموم می‌شه و از فهرست می‌ره.",
    leaveResumable: "خروج",
    endGame: "پایان بازی",
    // Two-option ONLINE mid-game exit: «خروج» keeps the seat reclaimable (jump back in via
    // «ادامه بازی»); «خروج بدون بازگشت» is permanent (a bot takes the seat for good).
    exitOnlineBody:
      "با «خروج» جات به ربات سپرده می‌شه ولی می‌تونی از «ادامه بازی» برگردی. با «خروج بدون بازگشت» برای همیشه از این بازی خارج می‌شی.",
    leaveOnlineResumable: "خروج",
    leaveOnlineForever: "خروج بدون بازگشت",
    /*
     * «انصراف» — the PRIVATE table for TWO (operator, session B item 4, 2026-10-08). There
     * «خروج بدون بازگشت» was never «a bot takes my seat and the others play on»: there are no
     * others, and the server FORFEITS (a rated loss the private leaver was never told about). So
     * the button is «انصراف», and — «انصراف» being this app's word for CANCEL everywhere else — it
     * opens a SECOND confirmation whose body says what the server will do, from the server's own
     * predicate (`OnlineGame`'s `noRealOpponentNow`). The reader's own cost only.
     * Four-seat private tables keep «خروج بدون بازگشت» and {@link exitOnlineBody}.
     */
    exitOnlineTwoSeatBody:
      "با «خروج» جات به ربات سپرده می‌شه ولی می‌تونی از «ادامه بازی» برگردی. با «انصراف» برای همیشه از این بازی خارج می‌شی.",
    resign: "انصراف",
    resignTitle: "از بازی انصراف می‌دی؟",
    resignLossBody: "بازی همین‌جا تموم می‌شه و باخت به نام تو ثبت می‌شه.",
    resignFreeBody: "از میز خارج می‌شی و نتیجه‌ای ثبت نمی‌شه.",
    resignConfirm: "بله، انصراف می‌دم",
    resignCancel: "نه، ادامه می‌دم",
    // PUBLIC table, mid-game: there is only ONE exit and it costs a loss, so the dialog says so
    // BEFORE they take it. (A private table keeps the two-option dialog above.)
    exitPublicTitle: "از بازی خارج می‌شوی؟",
    exitPublicBody:
      "میز عمومی بازگشت ندارد: با خروج، جایت را ربات می‌گیرد و این بازی برایت باخت ثبت می‌شود.\nاگر چند بار پشت‌سرهم از بازی‌های عمومی خارج شوی، مدتی نمی‌توانی بازی عمومی جدید شروع کنی.",
    /**
     * NOBODY REAL TO ABANDON (2026-08-28, widened 2026-08-29) — the same single exit, minus the
     * claim it costs anything.
     *
     * ⚠️ THIS DIALOG IS THE ONLY PLACE THE PLAYER IS TOLD BEFOREHAND, so it must not keep promising
     * a loss the server will not record. It must ALSO not read as a free undo: the seat is still a
     * bot's for good, exactly as before — only the punishment is dropped. The SERVER decides
     * (`TableController.leaveIsFree`); this sentence just has to agree with it.
     *
     * ⚠️ THE OLD WORDING SAID TWO THINGS THAT ARE NOW FALSE. «بقیهٔ بازیکن‌ها رفته‌اند و فقط
     * ربات‌ها مانده‌اند» is wrong when a یار is still sitting at the table — under the widened rule
     * only my OPPONENTS have to be bots — and wrong again on a table that simply never dealt four
     * humans, where nobody has left at all. «این بازی همین‌جا تمام می‌شود» was wrong in the same
     * two cases: the game carries on for whoever is still there. So the reason is no longer
     * narrated; the sentence says what is true in every case it can appear in.
     */
    exitPublicLastBody:
      "این‌جا حریف واقعی‌ای نداری که رهایش کنی، برای همین این خروج برایت باخت ثبت نمی‌شود.\nولی بازگشتی هم ندارد: جایت را ربات می‌گیرد و دیگر نمی‌توانی به این بازی برگردی.",
    leavePublic: "بله، خارج می‌شوم",
    stayPublic: "برمی‌گردم به بازی",
    // «پایان بازی» failed (the server never confirmed the delete): the game is
    // still in the in-progress list — offer a retry or a return to the game.
    endFailedTitle: "پایان بازی انجام نشد",
    endFailedBody:
      "تأیید از سرور نرسید و بازی هنوز توی فهرست بازی‌های در جریان می‌مونه. یه بار دیگه امتحان کن.",
    endRetry: "تلاش دوباره",
  },
  hearts: {
    title: "بی‌دل",
    connecting: "در حال اتصال…",
    yourTurn: "نوبت شماست",
    waiting: "منتظر بازیکنان…",
    passPrompt: "۳ کارت برای رد کردن انتخاب کنید",
    receivedCards: "۳ کارت گرفتی",
    heartsBroken: "دل‌ها شکسته شد",
    you: "شما",
    bot: "ربات",
    scores: "امتیازها",
    gameOver: "پایان بازی",
    youWon: "شما بردید! 🏆",
    youLost: "این بار نشد",
    /** بی‌دل is INDIVIDUAL and lowest wins, so two seats CAN finish level at the top — and until
     *  2026-08-29 each of them was told «شما بردید». A shared first place is a draw, not a win. */
    drawn: "مساوی شدید! 🤝",
    /** Sub-line under {@link drawn}: names the shared total, so the tie is legible at a glance.
     *  Deliberately NOT «هر دو» — three or four seats can share the minimum too. */
    drawnHint: (points: string): string => `مشترکاً اول، با ${points} امتیاز`,
    playAgain: "بازی دوباره",
    leave: "خروج",
    connectionError: "خطا در اتصال به سرور بازی",
  },
  online: {
    title: "بازی آنلاین بی‌دل",
    play: "بازی آنلاین",
    badge: "آنلاین",
    connecting: "در حال اتصال به بازی آنلاین…",
    waiting: "منتظر بقیه بازیکن‌ها…",
    starting: "داریم شروع می‌کنیم…",
    countdown: "شمارش معکوس",
    seatEmpty: "منتظر…",
    you: "شما",
    bot: "ربات",
    leave: "خروج",
    connectionError: "خطا در اتصال به بازی آنلاین",
    // Quick-match vs. private chooser
    chooseMode: "نوع بازی رو انتخاب کن",
    quickMatch: "بازی سریع",
    quickMatchHint: "با بازیکنان تصادفی",
    private: "میز خصوصی",
    privateHint: "با دوستان خودت",
    // Game Settings screen
    settings: "تنظیمات میز",
    createTable: "میز بساز",
    turnTimer: "زمان هر نوبت",
    seconds: "ثانیه",
    // Owner-set table policies (chosen at create time; shown read-only to everyone
    // in the waiting room). The retired «مکث/ربات» abandon policy + «تکمیل با ربات»
    // toggle are gone: a dropped seat always becomes a reclaimable bot, and the host
    // may start a partial table with bots at any time.
    muteTable: "بی‌صدا کردن میز",
    muteTableHint: "صدای بازی برای همهٔ بازیکنان این میز خاموش می‌شه",
    chatEnabled: "گفتگوی میز",
    chatEnabledHint: "بازیکنان می‌تونن داخل میز با هم چت کنن",
    // Card-deck ("skin") picker — the back design every seat at the table sees.
    cardDeck: "طرح کارت‌ها",
    cardDeckHint: "پشت کارت‌ها برای همهٔ بازیکنان این میز",
    // Short labels for the waiting-room settings chips.
    sound: "صدا",
    chatShort: "گفتگو",
    deckShort: "طرح کارت",
    on: "روشن",
    off: "خاموش",
    back: "بازگشت",
    // Private waiting room
    host: "میزبان",
    // The host walked out of the WAITING room: the table is over before it began. The
    // guests came for THEIR invite, so they are told who closed it — then sent back.
    tableClosedTitle: "میز بسته شد",
    tableClosedBy: (name: string) => `${name} میز را بست.`,
    tableClosedByHost: "میزبان میز را بست.",
    // MID-GAME the host did not merely close an empty table — they quit a game that was being
    // played, which ends it for everyone. Saying only «میز بسته شد» leaves a player whose hand
    // just vanished with no idea what happened, so the reason is spelled out.
    tableClosedMidGameTitle: "بازی تمام شد",
    tableClosedMidGameBy: (name: string) =>
      `${name} (میزبان) بازی را ترک کرد و میز بسته شد.\nاین بازی ادامه پیدا نمی‌کند.`,
    tableClosedMidGameByHost: "میزبان بازی را ترک کرد و میز بسته شد.\nاین بازی ادامه پیدا نمی‌کند.",
    // The table was never started within the idle window → auto-closed.
    tableExpiredTitle: "میز منقضی شد",
    tableExpiredBody: "این میز مدتی بی‌استفاده ماند و به‌صورت خودکار بسته شد.",
    // Tooltip on the host's waiting-room idle countdown chip.
    idleCloseHint: "تا بسته‌شدن خودکار میز",
    // FEATURE 3 — the owner's mid-game "invite a friend into this bot chair" picker.
    seatInviteTitle: (): string => "دعوت دوست به جای ربات",
    seatInviteHint: "یکی از دوستات رو انتخاب کن تا جای این ربات بشینه.",
    seatInviteEmpty: "دوستی برای دعوت پیدا نشد.",
    // PUBLIC quick-match tables — the per-seat «+». A public table is ownerless, so this is
    // available to ANY seated player, unlike the host-only panel on a private table.
    seatInvitePublic: "دعوت دوست به این صندلی",
    seatInviteHintPublic: "برای دعوت دوستت به میز، روی + بزن",
    seatInvitePending: (name: string): string => `دعوت برای «${name}» فرستاده شد`,
    // A PUBLIC table with nobody to play against yet. The fill countdown deliberately does not
    // run while one person sits alone (it could only ever end in bots), so the waiting room has
    // to SAY that — a screen with no clock and no explanation reads as a hang.
    waitingForReal: "منتظر بازیکن واقعی",
    waitingForRealHint: "شمارش شروع بازی با اومدن نفر دوم راه می‌افته",
    countdownHeldHint: "شمارش نگه داشته شده؛ با اومدن نفر بعدی از همین‌جا ادامه می‌ده",
    /** …and the way out, said plainly: bots are what SOLO is for. Leaves this table. */
    playSoloInstead: "بازی تک‌نفره",
    /** Closes a "why you could not join" notice. «خروج» read as "leave the game" on a screen
     *  where there is no game to leave — this is an acknowledgement, not an exit. */
    acknowledge: "باشه",
    /** A NEWER COPY OF THE APP TOOK THIS SEAT BACK (card-74) — what the copy that was let go shows,
     *  once, on the «why you could not join» screen: the game moved to the newer window (the operator,
     *  2026-10-20). The very sentence the game-server sends a copy on an older bundle. */
    takenOver: SEAT_MOVED_MESSAGE,
    /*
     * «بازی دوباره» ON A PRIVATE TABLE IS THE HOST'S CALL (operator, 2026-09-09).
     *
     * ⚠️ ONE LINE, and it opens with a Persian word — no leading emoji and no Latin name, so it
     * needs no bidi marks (see `## Gotchas`: a line that OPENS with a strong-L character resolves
     * LTR on every client, and a squared-Latin emoji is strong L wearing a picture).
     */
    rematchWaiting: "میزبان باید بازی بعدی رو شروع کنه",
    /*
     * …AND WHAT REPLACES IT THE MOMENT THE OFFER LANDS (operator, 2026-09-10).
     *
     * ⚠️ THE LINE IS SWAPPED, NEVER JUST REMOVED. Nothing else announces a rematch — no sound, no
     * toast, no motion; `onRematchOffered` only sets `rematchRoomId` — so if this line merely
     * VANISHED the whole arrival would be a button going from 60% to 100% opacity and shifting
     * 16px. A guest who was not staring at the button would have no way to tell the offer had
     * come, and the successor is reaped by `IDLE_TABLE_TIMEOUT_MS` ten minutes later.
     *
     * ⚠️ Shown to the GUEST only: it speaks about the host in the third person, so on the host's
     * own card it would describe them to themselves. The host never had the waiting line either,
     * so for them nothing is being swapped.
     *
     * Same bidi reasoning as the line above: opens with a Persian word, no leading emoji and no
     * Latin name, so it needs no marks.
     */
    rematchOffered: "میزبان بازی بعدی رو شروع کرد",
    /*
     * …AND WHY THERE IS NO BUTTON AT ALL (operator, session B, 2026-10-08): a rematch is shown only
     * when it can work, and when it cannot, the button goes and ONE of these says why. Facts about
     * the TABLE — or the reader's own connection — and never what anybody's leaving cost them
     * (`away-copy-rule.test.ts`). Same bidi shape as the two lines above: each opens with a Persian
     * word, no emoji, no Latin name.
     */
    rematchOwnerLeft: "میزبان میز رو ترک کرد",
    rematchOthersLeft: "بقیه بازیکن‌ها رفتند",
    rematchConnectionLost: "ارتباطت با این میز قطع شد",
    backToLobby: "بازگشت به لابی",
    start: "بزن بریم!",
    waitingForHost: "منتظریم میزبان شروع کنه",
    invite: "دعوت دوستان",
    inviteNote: "این لینک رو برای دوستات بفرست یا از فهرست دوستان دعوتشون کن",
    copyLink: "کپی لینک",
    copied: "کپی شد",
    /** The copy really can be refused — Telegram's WebView rejects `writeText` when the document
     *  is not focused — and saying «کپی شد» anyway sends the host off to paste nothing. */
    copyFailed: "کپی نشد",
    // In-game lifecycle
    turnLeft: "زمان نوبت",
    yourTurn: "نوبت شماست",
    paused: "بازی متوقف شد",
    resumeWithBot: "ادامه با ربات",
    /**
     * THE WAY OUT OF THE OPPONENT-AWAY SCRIM (2026-09-14) — game-neutral, so it lives here rather
     * than in `chess`/`backgammon`, which carry only the wording that differs per game.
     *
     * `OpponentAwayOverlay` covers the whole screen (`fixed; inset:0; zIndex:40`, no
     * `pointer-events:none`) and both two-seat tables keep their exit button UNDER it — so for the
     * entire time a seat was away the only thing a player could tap was the chat toggle at
     * `z-[55]`. The second line is not decoration: the confirm dialog behind this button used to
     * promise a recorded loss, and the server used to record one.
     */
    /* ⚠️ `awayLeave` / `awayLeaveFree` / `awayLeaveCosts` WERE DELETED ON 2026-09-15 with the scrim
       that carried them. The away overlay no longer offers an exit of its own — it is a
       non-blocking pill now and the table's own exit is reachable underneath — so the caption
       under that button had nowhere left to render. What tells the survivor they may go is
       `awayFreeExitNotice` below, and what states the cost is the exit dialog itself. */
    /**
     * THE FREE EXIT, ANNOUNCED ON THE TABLE ITSELF (operator, 2026-09-15) — the one line that tells
     * the survivor they may go, so it is STATE-DRIVEN and never on a timer: it stands for exactly
     * as long as the exit is actually free, because a notice that fades is no use to somebody who
     * looked away.
     *
     * ⚠️ IT SAYS WHAT IS TRUE FOR THE READER AND NOTHING ABOUT WHAT THE ABSENCE COST THE OTHER
     * PLAYER. «بدون جریمه» is the reader's own penalty. Nothing on this path may state or imply
     * that the player who vanished got off without cost — that teaches people closing the app is
     * free, and they will use it.
     */
    awayFreeExitNotice: "حریف شما برای ۶۰ ثانیه متوالی حضور نداشت — می‌توانید بدون جریمه خارج شوید",
    /**
     * CARD-98 2.1 (operator, 2026-10-28, verbatim) — a confirmed «خروج» answers on screen at once.
     * On a stalling connection the leave waits for a live socket and then for the server's verdict,
     * and the table looked dead the whole time («pressing exit does nothing»). Says only that the
     * exit is under way; the waits themselves are unchanged.
     */
    leaving: "در حال خروج…",
    /** …and while the grace is still running: information, not an invitation. */
    awayDisconnectedNotice: (name: string): string => `ارتباط «${name}» قطع شد…`,
    playerDisconnected: (name: string): string => `بازیکن ${name} قطع شد…`,
    replacedByBot: (name: string): string => `«${name}» از میز رفت؛ ربات جایگزینش شد`,
    /**
     * THEIR CONNECTION DROPPED — they did not leave.
     *
     * The server has always said which of the two happened (`left.reason` is
     * `"disconnected"` vs `"left"`), but the notice read the same either way, so a player
     * whose internet died was announced to the table as having walked out. On a mobile
     * network that is the COMMON case, and it is the one that most needs telling apart:
     * «رفت» is a decision, «قطع شد» is an accident.
     */
    disconnectedByBot: (name: string): string => `ارتباط «${name}» قطع شد؛ ربات جایگزینش شد`,
    /** The OWNER's connection dropped mid-game. Two lines, like {@link hostLeftBot}. */
    hostDisconnectedBot: (name: string): string =>
      `ارتباط میزبان «${name}» قطع شد\nربات جایگزینش شد و بازی ادامه دارد`,
    /** FEATURE 3 — pushed to a player whose LEFT (still-reclaimable) seat the owner filled
     *  with a friend (`name` = that friend): the game is over for them, and it drops from
     *  «ادامه بازی». */
    replacedNotice: (name: string): string => `«${name}» جایگزینت شد؛ این بازی برات تموم شد`,
    /** The OWNER walked out mid-game: a bot has their chair and — the part everyone
     *  needs — the game goes on. Two clean lines (rendered with `pre-line`): who left, then
     *  what happens. */
    hostLeftBot: (name: string): string =>
      `میزبان «${name}» از میز رفت\nربات جایگزینش شد و بازی ادامه دارد`,
    /** The OWNER walked out of the WAITING room: no bot, the seat is just free. */
    hostLeftLobby: (name: string): string => `میزبان «${name}» میز را ترک کرد`,
    /** Shown to whoever inherited the crown, so they know the table is theirs to start. */
    youAreHostNow: "حالا میزبان میز تو هستی",
    leftTable: (name: string): string => `«${name}» از میز رفت`,
    /**
     * A PUBLIC table removed somebody for inactivity. It must NOT say «از میز رفت» — they did not
     * leave, and telling three other players that a person who is still sitting there walked out
     * was half of the 2026-07-23 bug.
     */
    idleReplacedByBot: (name: string): string => `«${name}» چند دور بازی نکرد؛ ربات جایگزینش شد`,
    /** Shown to the REMOVED player themselves — addressed to them, not about them. */
    idleRemovedTitle: "از میز کنار گذاشته شدی",
    idleRemovedBody:
      "چند دور پشت‌سرهم بازی نکردی و ربات جای تو را گرفت تا بازی برای بقیه متوقف نشود.\nاین بازی برای تو تمام شد؛ می‌توانی به لابی برگردی و یک بازی تازه شروع کنی.",
    idleRemovedAck: "باشه، برگرد به لابی",
    /* Shown to the player the HOST removed («اخراج», 2026-09-02) — addressed to them. Separate
       wording from the idle removal on purpose: nothing they did caused this, and «you were
       inactive» would be simply untrue. */
    kickedTitle: "از میز اخراج شدی",
    kickedBody:
      "میزبان تو را از این میز کنار گذاشت و جایت را ربات گرفت.\nنمی‌توانی به این میز برگردی؛ می‌توانی به لابی برگردی و یک بازی تازه شروع کنی.",
    kickedAck: "باشه، برگرد به لابی",
    /**
     * PUBLIC-TABLE QUIT (2026-08-27) — what it cost, said AFTER the exit. The count is the part
     * that changes behaviour, so it is always named; the lockout line is added only when this
     * leave is the one that triggered it.
     *
     * ⚠️ «هم‌تیمی‌ات باختی نمی‌گیرد» CAME OUT ON 2026-09-15 (operator), AND THE RULE IT BROKE IS
     * WIDER THAN THIS ONE LINE: **no screen, in any game, may state what a departure or a
     * disconnect did or did not cost the OTHER player.** Say what is happening to the person
     * reading it, and stop there.
     *
     * A previous pass found this exact clause, and kept it on the reasoning that it is about a
     * TEAMMATE on a partnership table after a deliberate walk-out and so «teaches nothing about
     * disconnecting». That is the narrow reading: a player who learns here that somebody ELSE's
     * record was spared has been told the shape of the rule, and «close the app and nobody pays»
     * is one step from it. The second half — that the game goes on for the others — is a fact
     * about the TABLE, not about anybody's record, and stays.
     *
     * The sweep that followed read every Persian string in `apps/web/src/i18n`, `apps/bot/src`,
     * `apps/web/src/content`, `packages/shared/src`, `apps/api/src` and `apps/game-server/src`.
     * This was the only line that said it; `awayFreeExitNotice`, `exitPublicLastBody`,
     * `chess.awayFreeExit` and `seatExpiredBody` all speak about the reader's own cost and stay
     * exactly as they are. Pinned by `public-leave-penalty.test.tsx`.
     */
    leavePenaltyTitle: "این بازی به‌عنوان باخت ثبت شد",
    leavePenaltyBody:
      "چون وسط بازی عمومی از میز خارج شدی، این بازی برایت باخت حساب می‌شود.\nبازی برای بقیه ادامه دارد.",
    leavePenaltyCount: (n: number): string =>
      `تا اینجا ${faDigits(n)} بار امروز از بازی عمومی خارج شده‌ای.`,
    leavePenaltyLockout: (text: string): string =>
      `به همین دلیل تا ${text} نمی‌توانی بازی آنلاین عمومی شروع کنی.`,
    leavePenaltyAck: "باشه",
    /** The gate refused a public quick-match because they are inside a lockout. The BODY is the
     *  server's own sentence (it carries the remaining time) — never a second copy of the rule. */
    lockoutTitle: "فعلاً نمی‌توانی بازی عمومی شروع کنی",
    lockoutHint: "بازی تک‌نفره و میز خصوصی با دوستانت باز است.",
    lockoutAck: "متوجه شدم",
    /** A card table, DURING the reconnect grace: a small non-blocking pill, not an overlay —
     *  play continues (the seat is auto-played) so the other three must not be frozen. */
    reconnectingSeat: (name: string): string => `«${name}» در حال اتصال دوباره…`,
    /** The CARD tables say it on the dropped seat's own label instead (2026-09-15): the pill sat on
     *  the felt's top row. Short on purpose — MEASURED at the seat fonts, 64 px at 12 px bold; the
     *  «…» form was 73 px against حکم's 74 px label and 70 px on بی‌دل's. */
    seatReconnecting: "در حال اتصال",
    /** …and what that same label says once the chair has changed hands, one per REASON — the whole
     *  sentence (`disconnectedByBot` and its siblings) stays as the seat's accessible name. Measured
     *  at 12 px bold: 40.2 / 50.0 / 42.5 px; «ارتباط قطع شد» (70.2) and «چند دور بازی نکرد» (83.1)
     *  did not fit. ⚠️ An accident is not a decision, and an idle removal is NEITHER. */
    seatDisconnected: "قطع شد",
    seatLeft: "از میز رفت",
    seatIdle: "بازی نکرد",
    /** A seat whose identity the roster could not resolve — a human name is never invented. */
    somePlayer: "یک بازیکن",
  },
  pasur: {
    title: "پاسور",
    connecting: "در حال اتصال…",
    yourTurn: "نوبت شماست",
    // Mid-game the pill names whose turn it is by SIDE — پاسور is two partnerships. It used to say
    // «منتظر بازیکنان…» whenever it was not your turn, which reads as a table still filling.
    partnerTurn: "نوبت یار",
    opponentTurn: "نوبت حریف",
    leave: "خروج",
    gameOver: "پایان بازی",
    playAgain: "بازی دوباره",
    you: "شما",
    opponent: "بازیکن",
    /** The tag on your partner's seat (the seat across — the engine's `sideOf`). */
    partner: "یار",
    /** The two sides, as حکم names them: your side, the other side. */
    us: "ما",
    them: "حریف",
    table: "زمین",
    captured: "برده‌ها",
    sweeps: "سور",
    youWon: "شما بردید! 🏆",
    youLost: "این بار نشد",
    scores: "امتیاز تیم‌ها",
    /** …the end card's heading at a table for TWO, where there are no teams: the match's result, as
     *  تخته نرد's end card names it («نتیجهٔ مسابقه: شما … حریف»). */
    matchResult: "نتیجهٔ مسابقه",
    // ── P1 Task 7: the end of a match (W18) — two TEAM rows, the winning side marked, the final deal's
    //    line, and why it ended, read the دوز way (`GameResult.reason`): a line per side, true for
    //    whoever reads it (the away-copy rule — `away-copy-rule.test.ts` lists each).
    /** The mark on the winning side's row of the end card. */
    winner: "برنده",
    /** The end card's line for the deal that ended the match — «آخرین دست: ما +۵ · حریف +۱۶». */
    finalDeal: "آخرین دست",
    /** A match the room ended because the other side is gone («opponents-left»), as the winners read
     *  it — a fact about the table, never what it cost anybody. */
    endOpponentsLeft: "حریف‌ها دیگه سر میز نیستن",
    /** …and as the side that went reads it, should one of them see the card: their chairs are bots'. */
    endYourSideLeft: "جای شما و یارتون به ربات رسید",
    /** An idle removal at a table for two («idle»), as the winner reads it. */
    endOpponentIdle: "حریف چند نوبت پشت‌سرهم بازی نکرد",
    /** «خروج» at a table for two ENDS the match («resign», the two-seat forfeit) — as the winner reads
     *  it, a fact about the table, the other two-seat games' words. ONE string: the leaver's socket is
     *  closing, so nobody on that side is left to read a second. */
    endOpponentResigned: "حریف انصراف داد",
    /** …and as the removed player reads it. */
    endYouIdle: "چند نوبت پشت‌سرهم بازی نکردید",
    /** The match's target, said once beside the side totals («تا ۶۲»). */
    target: (points: number): string => `تا ${faDigits(points)}`,
    // ── The tables for two (operator, 2026-10-20): a size is a ROOM, picked by the host on «تنظیمات
    //    میز» (always opening on ۴) and, in public, by which of the two «اتاق عمومی» buttons was tapped.
    /** A table's size, as the host's picker and the lobby's two public buttons BOTH say it («۴ نفره»)
     *  — one string, so the two surfaces cannot word the same size differently (تخته نرد's rule). */
    seatsOption: (n: string): string => `${n} نفره`,
    /** The host's picker, in the form of the other card-game pickers («بازی تا چند امتیاز؟»). */
    chooseSeats: "بازی چند نفره؟",
    /** …and what the two sizes are. */
    seatsHint: "۴ نفره یعنی دو تیمِ دونفره، ۲ نفره یعنی رو در رو.",
    /** The table's score strip, for assistive tech: the two sides' match totals. */
    matchScore: "امتیاز بازی",
    /** THE LIVE TALLIES under your name (Task 7, W17 — they replaced the last deal's «+n» line, D3):
     *  a side's clubs this deal, for assistive tech («خاج ما: ۸») — on screen a pip and the count, in
     *  the score box's colour (`PasurTable`'s TALLY_FORMS: a narrower column drops the side words,
     *  then the two cards, never a count). */
    talliesClubs: (side: string, n: number): string => `خاج ${side}: ${faDigits(n)}`,
    /** …and a card a side holds this deal — the 2♣ or the 10♦ — for assistive tech («۲ خاج: ما»). */
    talliesOwner: (card: string, side: string): string => `${card}: ${side}`,
    /** Your turn's prompt, at the felt's bottom — only ever on YOUR screen, on your move. */
    prompt: "یه کارت بازی کن یا جمع بزن",
    /** The capture picker (R4): a lifted card can take SEVERAL sets and none is chosen yet — the
     *  prompt asks which (every card of any set glows; a tap on one chooses its set). Short on
     *  purpose: it shares the felt's bottom row with «جمع کن» at 256 px. */
    pickSet: "کدوم دسته؟",
    /** THE GROUPS, EACH ITS OWN BUTTON (B3 item 2, operator 2026-10-20: «at «کدام دسته» I couldn't
     *  tell whether to pick a card on the table or pick the group»): a chip per set beside the
     *  question, the set's cards drawn as the tallies draw a card — its rank, then its suit's pip.
     *  A number card's rank is its Persian digits; an ace or a court card by its name. */
    setRank: (rank: string): string =>
      rank === "A"
        ? "آس"
        : rank === "J"
          ? "سرباز"
          : rank === "Q"
            ? "بی‌بی"
            : rank === "K"
              ? "شاه"
              : faDigits(rank === "T" ? 10 : Number(rank)),
    /** …a card's name for assistive tech («۷ دل», «بی‌بی خاج»). */
    setCard: (rank: string, suit: string): string => {
      const s = suit === "C" ? "خاج" : suit === "D" ? "خشت" : suit === "H" ? "دل" : "پیک";
      return `${fa.pasur.setRank(rank)} ${s}`;
    },
    /** …and a chip's name: which group, and the cards it takes («دستهٔ ۲: ۴ پیک، ۲ خاج»). */
    setOption: (n: number, cards: readonly string[]): string =>
      `دستهٔ ${faDigits(n)}: ${cards.join("، ")}`,
    /** The picker's confirm pill — plays the chosen capture. Greyed while the choice is open. */
    confirm: "جمع کن",
    /** …its name for assistive tech when ready: how many table cards the chosen set takes. */
    confirmTakes: (n: number): string => `جمع کن: ${faDigits(n)} کارت از زمین`,
    /** …and while it waits for a choice. */
    confirmWaiting: "جمع کن: اول یه دسته رو انتخاب کن",
    /** The dealer marker's accessible name — the marker rides the avatar of the seat that dealt this
     *  deal, so the seat beside it says who (card-a7 B11: it replaced the felt's corner chip
     *  «دست‌دهنده»); the guide's word for the dealer, as شلم's marker says it. */
    dealer: "کارت‌دهنده",
    /** The felt's corner chip in a deal's final round (D8: the stock is empty, so no capture there
     *  makes a سور — the chip says so; it wraps to two lines where its corner is narrow). Two NO-BREAK
     *  spaces hold «آخر —» and «سور ندارد» whole, so it wraps after the dash (and, at its narrowest,
     *  after «دست»), never inside «سور ندارد»: with plain spaces its first line at 384 and 390 under
     *  ×1.3 read «دست آخر — سور» — a سور announced (Task 5 fix round 1, F3's shots). */
    finalRound: "دست آخر\u00a0— سور\u00a0ندارد",
    /** …its SHORT form, where the whole chip would take a third line in its corner (Task 8b: 300×667
     *  under ×1.5, 71.5 px tall, into the top seat's name) — the corner says what it can; assistive
     *  tech always hears `finalRound` whole. */
    finalRoundShort: "دست آخر",
    /** A SIDE seat's drop-grace notice in its SHORT form (Task 8b) — where «در حال اتصال»
     *  (`fa.online.seatReconnecting`) would take a THIRD line in the 52 px side box (320×568 and
     *  300×667 under ×1.5: 76 px tall). The notice's label — the whole sentence — is unchanged. */
    noticeReconnecting: "اتصال…",
    /** …and its SHORTEST, where even «اتصال…» would not stay on ONE line (Task 8b follow-up: at the
     *  52 px floor under ×1.5 its «…» broke off onto a second line alone; the word fits whole). */
    noticeReconnectingWord: "اتصال",
    connectionError: "خطا در اتصال به سرور بازی",
    // ── P1 Task 5: the moments (W6, W16) — each names ONE player, true for every viewer: your own
    //    seat reads «شما» (`you`). The name is a bidi ISOLATE (`isolate`, FSI…PDI): a Latin name — or
    //    one cut short with «…» — keeps its own order inside the Persian line.
    /** The banner when a capture clears the table and scores a سور — who made it. */
    surScored: (name: string): string => `سور! — ${isolate(name)}`,
    /** The banner when a clear CANCELS a سور the other side holds (D10) — whose سور is gone: the
     *  holder's (the guide: «سورِ شما یکی از سورهای حریف را خنثی می‌کند»). */
    surCancelled: (name: string): string => `سور ${isolate(name)} خنثی شد`,
    /** …its LAST form, with no name: where not even the name's first letter fits the banner's room
     *  (the best-effort sizes under ×1.5) — a true statement, never a cut verb (ruled 2026-10-11). */
    surCancelledNameless: "سور خنثی شد",
    /** The label as a deal's remainder slides to its last capturer (D14). */
    scoopTo: (name: string): string => `باقی زمین به ${isolate(name)}`,
    /** The chip under a seat whose SIDE holds standing سورs this deal (only one side ever does). */
    surCount: (n: number): string => `سور ${faDigits(n)}`,
    /** …and what it says to assistive tech: whose side, how many. */
    surCountLabel: (side: string, n: number): string => `سور ${side}: ${faDigits(n)}`,
    // ── P1 Task 7: the deal's breakdown card (W15) — its lines named as the guide names them; every
    //    number from the deal's result (`lastDeal`) and the engine's values.
    /** The card, for assistive tech: the deal that just ended, scored. */
    breakdownTitle: "امتیاز این دست",
    breakdownClubs: "خاج",
    breakdownTwoOfClubs: "۲ خاج",
    breakdownTenOfDiamonds: "۱۰ خشت",
    breakdownAces: "آس",
    breakdownJacks: "سرباز",
    breakdownSurs: "سور",
    /** The deal's points per side («+۱۹»), under the lines. */
    breakdownDeal: "این دست",
    /** A line a side did not score. */
    breakdownNone: "—",
    /** «۸/۱۳» — the clubs a side took, of the deck's thirteen. */
    clubsOf: (n: number, of: number): string => `${faDigits(n)}/${faDigits(of)}`,
    /** …the clubs cell for assistive tech, which would read «۸/۱۳» and its points as one figure
     *  («۸/۱۳۷»: the arrow between them is drawn, not said) — «۸ از ۱۳، ۷ امتیاز». */
    clubsOfLabel: (n: number, of: number, points: number): string =>
      `${faDigits(n)} از ${faDigits(of)}` + (points > 0 ? `، ${faDigits(points)} امتیاز` : ""),
    /** «۱ × ۵» — a side's standing سورs times a سور's worth. */
    surTimes: (n: number, each: number): string => `${faDigits(n)} × ${faDigits(each)}`,
  },
  hokm: {
    /** حکم match length — chosen once, before the deal. */
    chooseMatchLength: "بازی تا چند دست؟",
    matchLengthHint: "هر تیم که زودتر به این تعداد دست برسد، برندهٔ مسابقه است.",
    matchLengthOption: (n: number) => `بازی تا ${n} دست`,
    roundsUnit: "دست",
    /**
     * کوت under one of the three SUIT-LESS calls (سرس / نرس / آس نرس) — a host setting, because
     * the sources treat it as a pre-game agreement. A خال call is unaffected, and the hint says
     * so: a host must not be left guessing whether this changes ordinary حکم.
     */
    modeKot: "کوت در سرس، نرس و آس نرس",
    modeKotHint:
      "اگر روشن باشد، بردن هر ۷ دست در این سه حالت ۲ امتیاز دارد. در حکم با خال فرقی نمی‌کند.",
    /** The waiting-room chip: the same setting, stated as a fact about THIS table. */
    modeKotOn: "کوت دارد",
    modeKotOff: "بدون کوت",
    /**
     * WHETHER THE HAKEM MAY CALL سرس / نرس / آس نرس AT ALL — a host setting (solo + private), and
     * the difference between the two PUBLIC rooms. DEFAULT OFF: the standard game.
     *
     * The hint names the three by name rather than saying «advanced», because «حکم‌های بی‌خال» is
     * a rule, not a difficulty — and it says what it costs (no کوت is a separate question, asked
     * only when this is on).
     */
    modes: "حکم‌های بی‌خال (سرس، نرس، آس نرس)",
    modesHint:
      "اگر روشن باشد، حاکم می‌تواند به‌جای خال یکی از این سه را بخواند: سرس (بالاترین کارت)، نرس (پایین‌ترین) یا آس نرس (آس پایین‌ترین).",
    /** The GUEST waiting-room grid's value — that grid pairs it with `modes` as a LABEL, but the
     *  cell is roomy, so it still states the setting as a fact about THIS table. */
    modesOn: "سرس/نرس دارد",
    /**
     * The HOST waiting-room's compact ICON row — the same fact, without the verb.
     *
     * MEASURED (real Vazirmatn, live DOM): the five chips need 362.8px, against a container of
     * 288 / 328 / 361px at 320 / 360 / 393 — so «سرس/نرس دارد» (98.3px) was the chip that pushed
     * the row onto a SECOND line, and the row above the felt is where the centre card needs the
     * height. Dropping the verb (78.5px) plus tightening the chip chrome brings the row to
     * 305.1px: ONE line at 360 and 393 (320 still wraps — there is no arrangement of five chips
     * that fits 288px, and see the note there for why it costs nothing).
     *
     * ⚠️ It still NAMES THE CALLS. This chip stands alone beside a bare ♠ glyph, so «دارد» on its
     * own would say nothing about WHICH setting is on — the pixels are not worth that.
     */
    modesShort: "سرس/نرس",
    /**
     * The SOLO start-screen switch's value. Its own label already names the three calls, so this
     * only answers «does this table have them?» — and it answers it the way `modeKotOn/Off` does,
     * because that is the switch DIRECTLY BENEATH it: two adjacent controls should not describe
     * the same kind of table setting in two different vocabularies («روشن/خاموش» is a device
     * state; «دارد/ندارد» is a fact about the game, which is what both of these are). Same
     * wording the admin card uses for this field, and the same width as what it replaces, so the
     * label beside it wraps no further.
     */
    modesSwitchOn: "دارد",
    modesSwitchOff: "ندارد",
    startGame: "شروع بازی",

    title: "حکم",
    connecting: "در حال اتصال…",
    yourTurn: "نوبت شماست",
    waiting: "منتظر بازیکنان…",
    /**
     * …and ANOTHER seat's move during play says WHOSE it is (card-a5 item 12; operator, 2026-10-11:
     * «During another player's turn, «منتظر بازیکنان…» becomes «نوبتِ [name]…»») — `waiting` stays for
     * a turn no seat holds. The line's two halves around the seat's NAME, which the strip draws in a
     * box of its own (`dir="auto"`, truncated: `TurnOfLine` in HokmTable.tsx). The kasra is the
     * operator's own — this screen otherwise carries no diacritics (see `chooseTrump`); the quote wins.
     */
    turnOf: { lead: "نوبتِ", tail: "…" },
    leave: "خروج",
    gameOver: "پایان بازی",
    playAgain: "بازی دوباره",
    you: "شما",
    opponent: "بازیکن",
    trump: "حکم",
    // The Hakem may now call a suit-less mode too, so this can no longer say «خال»
    // (and the kasra on «خالِ» was the only diacritic on this screen).
    chooseTrump: "حکم را انتخاب کنید",
    /**
     * The three SUIT-LESS calls, keyed by the engine's own ids — «حکم بی‌خال».
     * Written WITHOUT diacritics, deliberately (operator, 2026-08-25).
     * A contract test joins these keys to `HOKM_TRUMP_MODES` so the picker can
     * never be handed a call it has no name for.
     */
    trumpModes: {
      sares: "سرس",
      nares: "نرس",
      asnares: "آس نرس",
    } as Record<string, string>,
    hakem: "حاکم",
    selectingHakem: "در حال انتخاب حاکم…",
    // The call, to the three seats that are NOT the حاکم (operator, 2026-10-03: the strip says the
    // phase, not «منتظر بازیکنان…»). The حاکم themself reads `chooseTrump`.
    hakemChoosingTrump: "حاکم در حال انتخاب حکم…",
    // …the same, below 360 px wide, where the long line left the own name ~29 px (operator).
    hakemChoosingTrumpShort: "انتخاب حکم…",
    hakemChosen: "حاکم مشخص شد",
    youAreHakem: "شما حاکم شدید!",
    // The on-felt notice shown the beat a Hakem is revealed. Grammar per addressee:
    // «شما حاکم شدید» (2nd-person) when it is YOU, «{name} حاکم شد» (3rd-person) otherwise.
    becameHakem: (name: string, isYou = false): string =>
      isYou ? "شما حاکم شدید" : `${name} حاکم شد`,
    // Team labels on the felt HUD + trick counter: «ما» (us) / «حریف» (opponent).
    us: "ما",
    them: "حریف",
    // Prominent end-of-match banner naming the winning side.
    weWon: "تیم ما برد",
    theyWon: "حریف برد",
    team: "تیم",
    youWon: "تیم شما برد! 🏆",
    youLost: "تیم حریف برد",
    scores: "امتیاز تیم‌ها",
    connectionError: "خطا در اتصال به سرور بازی",
  },
  bluff: {
    title: "بلوف",
    connecting: "در حال اتصال…",
    yourTurn: "نوبت شماست",
    waiting: "منتظر بازیکنان…",
    leave: "خروج",
    gameOver: "پایان بازی",
    playAgain: "بازی دوباره",
    you: "شما",
    opponent: "بازیکن",
    claimRank: "ادعای رتبه",
    play: "بازی کن",
    call: "دروغ! (بلوف)",
    pass: "رد شدن",
    pile: "دسته",
    youWon: "شما بردید! 🏆",
    youLost: "این بار نشد",
    connectionError: "خطا در اتصال به سرور بازی",
  },
  shelem: {
    title: "شلم",
    connecting: "در حال اتصال…",
    yourTurn: "نوبت شماست",
    /** Not on the table since P1 Task 3 (W12: never «منتظر بازیکنان…» mid-game — the strip names
     *  whose turn it is, `partnerTurn` / `opponentTurn`); the layout harness still lists it. */
    waiting: "منتظر بازیکنان…",
    /** The strip's pill when another seat is to move: your partner, or an opponent. */
    partnerTurn: "نوبت یار",
    opponentTurn: "نوبت حریف",
    /** The header's line under «شلم» — the hand number, and only that (card-67 B1, the operator:
     *  «under the game name, only the hand number»): «دست ۳» («دست» is the hand, product-wide — حکم's
     *  score box says it the same way). */
    handOf: (n: string) => `دست ${n}`,
    /** The felt's one-line statement while the auction runs — «بالاترین: ۱۳۵ —», then the bidder's
     *  name in its own `dir="auto"` span (P1 Task 4); and the line before anyone has bid. */
    highest: (bid: string) => `بالاترین: ${bid} —`,
    /** The same statement in the centre's GAP form (a short felt — 320×568 under the bidding row —
     *  where the pill is no wider than the gap under the partner): the word «بالاترین:» dropped so
     *  the name keeps its room — «۱۳۵ —», then the name (the Task 4 review's ruling: with the full
     *  label the name read «Ba…» at 130 %). The full form stays wherever it fits. */
    highestShort: (bid: string) => `${bid} —`,
    noBidYet: "هنوز پیشنهادی نیست",
    /** The no-bid line where the felt's pill would CUT the whole one (Task 9b: 320×568 ×1.5). */
    noBidYetShort: "پیشنهادی نیست",
    /** The auction panel's slots row (its accessible name, card-67 C3): every seat's word so far. */
    bidHistory: "روند مزایده",
    /** The bidding row's confirm («ثبت ۱۳۵», «ثبت شلم»). */
    confirmBid: (label: string) => `ثبت ${label}`,
    /** The score box: the two teams' totals, toward the match target («تا ۱۲۰۰») — your team «ما»,
     *  the other «حریف», as حکم names them (card-a7 B11, the operator: «آنها» isn't colloquial). */
    us: "ما",
    them: "حریف",
    toTarget: "تا",
    /** The dealer marker's accessible name (the seat that dealt this hand). */
    dealer: "کارت‌دهنده",
    /** A bot's seat with no known player: «بات ۱», «بات ۲», «بات ۳». */
    bot: "بات",
    leave: "خروج",
    gameOver: "پایان بازی",
    playAgain: "بازی دوباره",
    you: "شما",
    opponent: "بازیکن",
    bid: "پیشنهاد",
    pass: "پاس",
    widow: "زمین",
    trump: "حکم",
    chooseTrump: "حکم را انتخاب کنید",
    /** …the same, where your strip has no room for it beside your «حاکم» chip (P1 Task 9a fix round 1,
     *  by measure): حکم's operator-approved short form (`fa.hokm.hakemChoosingTrumpShort`) without its
     *  ellipsis — it is YOUR move, and true here: your first card names trump. The حاکم's alone. */
    chooseTrumpShort: "انتخاب حکم",
    discard: "کنار بگذارید",
    /** …its SHORT form, by measure, where «کنار بگذارید (۲/۴)» leaves your name's column narrower
     *  than your «حاکم» chip (300×667 under a 150 % zoom — card-a7 B6): the count alone, «۲/۴» — the
     *  button right above says what to do («کنار بگذار»), the pill how far you are. The حاکم's alone. */
    discardShort: (picked: string, of: string) => `${picked}/${of}`,
    /** What every seat but the حاکم reads while the حاکم lays its four cards aside — a statement,
     *  never the order above, which is the حاکم's alone. حکم's gold notice at the felt's foot since
     *  card-67 B1 (it was the header's line under «شلم»). */
    declarerDiscards: (n: string) => `حاکم ${n} کارت کنار می‌ذاره`,
    /** The کف's confirm: lays the 4 selected cards down as one move. */
    discardConfirm: "کنار بگذار",
    /** What every seat but the حاکم reads as the کف flies to it: «{name} زمین را برداشت» — the name
     *  in its own `dir="auto"` span, then this (P1 Task 5). A statement: it orders nobody. */
    tookKaf: "زمین رو برداشت",
    /** THE REDEAL (D6; the final review B-I2): three opening passes deal the hand again — the felt's
     *  statement while the new hand deals in, after the third «پاس» has been seen on its seat. A
     *  statement, true for every viewer, and WHOLE in every form of the felt's pill (it wraps where
     *  one line would be cut). The operator's own sentence since card-98 (2026-10-27: «keep the «سه
     *  پاس» rule, and show a notice when it happens»): «سه», never «همه» — the fourth seat is never
     *  asked. U+061B after «پاس», a ZWNJ in «می‌شه». */
    redeal: "سه پاس؛ دست دوباره پخش می‌شه",
    /** …and your strip's word while it is read: the new auction waits for it, so the strip names no
     *  turn (until card-98 also the felt's GAP form of the line above, which dropped its words). */
    redealShort: "دوباره پخش",
    /** A SIDE seat's notices in their SHORT forms (Task 9b — `shelem-side-seat.tsx`): where the whole
     *  one would take a third line in the side box (50 px at 320, 41 at 300). «در حال اتصال» →
     *  «اتصال…», and where even that breaks: the word; «از میز رفت» → «رفت». The label (the whole
     *  sentence) is unchanged. */
    noticeReconnecting: "اتصال…",
    noticeReconnectingWord: "اتصال",
    noticeLeft: "رفت",
    /** The online guest's «میزبان باید بازی بعدی رو شروع کنه» in the end card's TIGHTER form (Task
     *  9b: 320×568 ×1.5, its two lines left the last hand 23 px under the fold). */
    rematchWaitingShort: "منتظر میزبان",
    /** The first card's announce, «حکم: ♠ — {name}»: this, the suit's pip, a dash and the حاکم's
     *  name in its own span — the first place the suit appears (P1-5). */
    trumpIs: "حکم:",
    /** The contract as one line — «قرارداد ۱۲۰», «قرارداد شلم» for a call (the hand-end card). */
    contractIs: (label: string) => `قرارداد ${label}`,
    /** THE BOTTOM BAR (the contract HUD — card-67 B3, the operator: «show only the contract and our
     *  points»), its two fixed slots' words: the contract, its value in a box of its own… */
    contract: "قرارداد",
    /** …and YOUR team's points taken this hand (card points + 5 a trick, never the کف until the hand
     *  ends — the engine's `pointsTaken`): the header's box shows the match totals. */
    ourPoints: "امتیاز ما",
    /** …and the OTHER team's, the same way (card-98 item 8, the operator: «add the opponents' points
     *  to the same line») — «حریف», as the score box names them (card-a7 B11: «آنها» isn't colloquial). */
    theirPoints: "امتیاز حریف",
    /** The HUD's accessible name. */
    contractHud: "قرارداد این دست",
    /** A gain as it is drawn on the felt — «+۳۳۰», the trick's «+۱۰» — in a `dir="ltr"` span, so its
     *  sign reads on the LEFT. */
    plusPoints: (n: string) => `+${n}`,
    /** A loss as it is drawn — «−۱۲۰», a TRUE minus (U+2212), in a `dir="ltr"` span: its sign on the
     *  LEFT, as the score box draws a negative total. */
    minusPoints: (n: string) => `−${n}`,
    /** THE HAND-END CARD (P1-7, P1 Task 7) — on the felt once the 12th trick is collected, until the
     *  next auction speaks: its accessible name, and your strip's word while the hand ends (the
     *  collect and the card: the next auction waits, so the strip names no turn — fix round 1)… */
    handEnd: "پایان دست",
    /** …the result per `lastHand.outcome`, the حاکم's team's line — its signed change after it in a
     *  `dir="ltr"` span: «قرارداد شد +۱۵۰» / «کم آورد −۱۲۰» / «شلم +۱۶۵» (all twelve, no call — card-98
     *  item 4) / «شلم خوانده −۳۳۰», and a call made «شلم خوانده +۳۳۰» (the brief's «… +۳۳۰»). A miss
     *  below the defenders (`yasa`) reads «کم آورد» too: it costs the bid like any miss (card-a7), and
     *  «یاسا»'s one Persian source means an extra −165 no rule here charges… */
    handResult: {
      made: "قرارداد شد",
      set: "کم آورد",
      yasa: "کم آورد",
      sweep: "شلم",
      shelemMade: "شلم خوانده",
      shelemFailed: "شلم خوانده",
    },
    /** …and the کف's four, revealed now (their accessible name). */
    kafRevealed: "کارت‌های زمین",
    /** THE END CARD (P1 Task 7, P2-4) — WHY the match ended, true for whoever reads it (the دوز way):
     *  the ROOM's reason first — a whole team gone (`"opponents-left"`, the partnership rule, the only
     *  reason a four-seat terminal carries; read by the team that stayed, whatever took the other's
     *  chairs: a «خروج», an idle removal, a lapsed grace)… */
    endTheyLeft: "تیم حریف دیگه سر میز نیست",
    /** …else the BOARD's: a team reached the target… */
    endWeReached: (target: string) => `تیم شما به ${target} رسید`,
    endTheyReached: (target: string) => `تیم حریف به ${target} رسید`,
    /** …the played-hand cap ended it, the higher total winning (D14)… */
    endCap: (hands: string) => `پایان ${hands} دست — امتیاز بیشتر برد`,
    /** …or an exact tie, which goes to the team that held the last hand's contract (D14). */
    endTie: "امتیازها برابر — تیم حاکمِ دستِ آخر برد",
    /** The last hand's line and the target, under the team rows (P2-4). ⏭ Drawn nowhere since card-98
     *  item 11 (the box, `lastHandTitle`); kept only because `away-copy-rule.test.ts` audits it — remove
     *  both together (review E-M5). */
    lastHand: "دست آخر:",
    /** …since card-98 item 11 a small BOX under the team rows (the operator: «who the حاکم was, what
     *  they bid, and each team's points gained or lost that hand»): its caption. */
    lastHandTitle: "دست آخر",
    playedTo: (target: string) => `بازی تا ${target}`,
    // The MATCH TARGET (audit D13) — a private host's «تنظیمات میز» choice and the solo start
    // screen's (card-67), ۶۶۰ or ۱۲۰۰ (default ۶۶۰), and the waiting room's read-only chip. A public
    // quick-match always plays ۶۶۰ and shows no picker.
    chooseTarget: "بازی تا چند امتیاز؟",
    targetOption: (n: string) => `${n} امتیاز`,
    targetHint: "تیمی که زودتر به این امتیاز برسه، مسابقه رو می‌بره.",
    /** The guest grid's label over «N امتیاز». */
    targetShort: "بازی تا",
    points: "امتیاز",
    team: "تیم",
    youWon: "تیم شما برد! 🏆",
    youLost: "تیم حریف برد",
    scores: "امتیاز تیم‌ها",
    connectionError: "خطا در اتصال به سرور بازی",
  },
  chess: {
    title: "شطرنج",
    connecting: "در حال اتصال…",
    yourTurn: "نوبت شماست",
    opponentTurn: "نوبت حریف",
    thinking: "حریف در حال فکر کردن…",
    waiting: "منتظر بازیکنان…",
    leave: "خروج",
    gameOver: "پایان بازی",
    playAgain: "بازی دوباره",
    you: "شما",
    opponent: "ربات",
    // ── THE OTHER CHAIR IS EMPTY (a resumed online game whose opponent has not come back) ──
    // `opponent` above is the SOLO default and is only true once a bot really sits down. Until
    // then the chair belongs to a named person who is simply not here, and saying «ربات» told
    // one player the bot had hung while she waited 25 minutes across 13 app opens (2026-09-04).
    /** The chip's marker beside the absent player's own name. */
    awayTag: "سر میز نیست",
    /** Fallback NAME for an absent chair whose owner the server could not resolve — «حریف», the
     *  word the turn line already uses. Never «ربات»: there is no bot in that chair yet. */
    awayName: "حریف",
    /** The on-table notice. Line 1 says what happened; line 2 says how it ends on its own. */
    awayTitle: "حریفت سر میز نیست",
    awayBody: "اگر برنگردد، یک ربات جای او می‌نشیند و بازی ادامه پیدا می‌کند.",
    /** THE FREE EXIT, ON THE SCREEN ITSELF — it was correct in the confirm dialog and invisible
     *  until you opened it, one tap behind a «خروج» that looked like resigning. */
    awayFreeExit: "می‌توانی همین حالا از میز بیرون بروی — هیچ باختی برایت ثبت نمی‌شود.",
    // Solo difficulty — chosen on the START screen, LOCKED for the game (chess-specific).
    difficulty: "سطح حریف",
    chooseDifficulty: "سطح حریفت رو انتخاب کن",
    difficultyLocked: "سطح حریف برای این بازی ثابت است",
    startGame: "شروع بازی",
    difficultyRestart: "با تغییر سطح، این بازی رها می‌شود و بازی تازه‌ای با این سطح شروع می‌شود.",
    difficultyStart: "شروع کن",
    // On-board states (side-to-move perspective). `check` = the checked side is on turn.
    check: "کیش",
    checkmate: "کیش و مات",
    stalemate: "پات",
    draw: "مساوی",
    // Draw reasons (from `view.drawReason`).
    drawFifty: "قانون پنجاه حرکت",
    drawThreefold: "تکرار سه‌باره",
    drawInsufficient: "مهرهٔ ناکافی",
    // End-of-game per-player outcome (chess has three: win / lose / draw).
    youWin: "بردی!",
    youLose: "باختی",
    drawResult: "مساوی شد",
    // WHY the room ended the game, when the board did not — shown under the result, so a win
    // that arrived out of nowhere (an opponent walking out) explains itself.
    // «resign» is the two-seat «انصراف» (session B item 4): the table fact, said as the button says it.
    endResign: "حریفت انصراف داد",
    /**
     * `GameResult.reason === "idle"` — the TABLE removed a seat whose turns kept lapsing, which
     * at two seats ends the match (2026-09-21). Two strings, unlike `endResign`'s one: a player
     * who resigns has left and never reads their own card, but a player the table removed is
     * STILL SITTING THERE and would otherwise be told their opponent walked out.
     *
     * ⚠️ UNREACHABLE ON شطرنج TODAY and deliberately present anyway — شطرنج is on no branch of
     * `chargeIdleMiss`'s `removable` (it never auto-plays a turn), but `ChessEndReason` carries
     * `idle` so the day it joins that list is not the day this card says nothing.
     */
    endIdle: "حریفت چند نوبت پشت‌سرهم بازی نکرد",
    endIdleLost: "چند نوبت پشت‌سرهم بازی نکردی",
    endTimeout: "وقت تموم شد",
    endClaim: "چون حریفت برنگشت، نتیجه به نفع تو ثبت شد",
    // Promotion picker.
    promotion: "ارتقا",
    promotionPrompt: "مهرهٔ ارتقا را انتخاب کن",
    // Piece names (promotion picker + captured-tray labels).
    queen: "وزیر",
    rook: "رخ",
    bishop: "فیل",
    knight: "اسب",
    king: "شاه",
    pawn: "پیاده",
    // Panels.
    moves: "حرکت‌ها",
    captured: "مهره‌های گرفته‌شده",
    // The per-side time bank chip (online timed tables); mm:ss in Persian digits.
    clock: "زمان",
    connectionError: "خطا در اتصال به سرور بازی",
    // Online host settings (chess private tables) — the host's colour, the time control,
    // and the disconnect policy; also the read-only chips in the waiting room.
    color: "رنگ مهره‌ها",
    colorWhite: "سفید",
    colorBlack: "سیاه",
    colorRandom: "تصادفی",
    timeControl: "زمان‌بندی",
    // ITEM 11 — one short word so all five time controls fit on ONE line («بدون زمان» wrapped).
    untimed: "آزاد",
    // ITEM 11 — «۱۵+۱۰» is opaque until someone tells you which number is which.
    // ITEM 9 — TWO lines, one per number, «عدد دوم» on the BOTTOM. One long line wrapped
    // wherever the box happened to end, splitting the sentence mid-clause.
    timeControlHint: "عدد اول: کل زمان هر بازیکن (دقیقه)",
    timeControlHint2: "عدد دوم: ثانیه‌ای که بعد از هر حرکت اضافه می‌شود",
    disconnect: "قطع اتصال",
    dcClaim: "ادعای برد",
    dcBot: "جایگزینی ربات",
    dcPause: "توقف",
    // Phase 3c — online disconnect overlays (shown to the PRESENT player when the opponent drops).
    opponentReconnecting: "حریف قطع شد؛ منتظر اتصال دوباره…",
    opponentPaused: "بازی متوقف شد؛ منتظر حریف…",
    opponentLeft: "حریف از میز رفت",
    waitingReturn: "منتظر برگشت حریف…",
    claimWin: "ادعای برد",
    claimHint: "می‌تونی برنده رو ادعا کنی یا منتظر برگشتش بمونی",
  },
  // Backgammon (تخته نرد) — the SOLO control + result strings (Phase 2). The roll pill MUST
  // read «تاس …» and the confirm pill «… تأیید …» (the table tests find them by /تاس/ and /تأیید/).
  /* ---- دوز (Connect Four) --------------------------------------------------------------- *
   * Two players, a 7×6 grid, four in a row. The Persian name is the operator's: «دوز» names
   * alignment games generally in Persian, and this is the four-in-a-row one.
   * ---------------------------------------------------------------------------------------- */
  dooz: {
    title: "دوز",
    leave: "خروج",
    connecting: "در حال اتصال…",
    connectionError: "خطا در اتصال به بازی",
    you: "شما",
    opponent: "حریف",
    // The start screen asks three things: colour, opponent level, and how many دست.
    chooseSettings: "قبل از شروع",
    startGame: "شروع بازی",
    color: "رنگ مهره‌های تو",
    colorHint: "حریف با رنگ دیگر بازی می‌کند",
    colorNames: {
      red: "قرمز",
      yellow: "زرد",
    },
    difficulty: "سطح حریف",
    hands: "تعداد دست",
    hands3: "۳ دست",
    hands5: "۵ دست",
    // THE OPENING CONTEST. Each player throws their OWN die and the higher one opens; a tie is
    // thrown again. It used to be decided inside the deal with nothing to throw and a banner that
    // stated the result — and, through a stuck effect, never went away.
    rollTitle: "کی شروع کنه؟",
    rollCta: "تاس بریز",
    rollWaitingYou: "تاس بریز تا معلوم بشه کی شروع می‌کنه",
    rollWaitingThem: "منتظر تاس حریف…",
    rollTied: "مساوی شد، دوباره بریزید",
    /** The score box in the header: «شما» and «حریف» over their two numbers. */
    scoreYou: "شما",
    scoreThem: "حریف",
    dieYouStart: "تاس با تو افتاد — تو شروع می‌کنی",
    dieTheyStart: "تاس با حریف افتاد — اون شروع می‌کنه",
    // Whose turn it is, under the board. The player must never have to guess.
    turnYours: "نوبت توئه — یک ستون رو نگه دار و رها کن",
    turnTheirs: "نوبت حریف…",
    // Between hands.
    handWonByYou: "دست {hand} رو بردی!",
    handWonByThem: "دست {hand} رو باختی",
    handDrawn: "دست {hand} مساوی شد",
    // The score box's FIRST column (2026-10-08, «the way حکم's header is»): «دست» over «۱ از ۳».
    handLabel: "دست",
    handOf: "{hand} از {total}",
    // The end card.
    gameOver: "پایان بازی",
    youWin: "بردی!",
    youLose: "باختی!",
    itsADraw: "مساوی شد!",
    // WHY it ended. The card used to say «چهارتا رو ردیف کردی» for EVERY win — including one
    // handed to you by an opponent who walked out, and one won on hands after a drawn last
    // board. Same idiom as تخته نرد's card: the ROOM's reason wins when the BOARD has none,
    // and the board's own reason distinguishes a four from a full board. [Reported 2026-09-04.]
    winSubtitle: "چهارتا رو ردیف کردی 🎉",
    loseSubtitle: "این دفعه نشد — دوباره تلاش کن!",
    drawSubtitle: "هیچ‌کدوم جلو نزدید — دوباره؟",
    /** The deciding دست filled the board with no four, so the MATCH was decided on دست‌ها. */
    winOnHands: "دست‌های بیشتری بردی 🎉",
    loseOnHands: "حریفت دست‌های بیشتری برد",
    /** `GameResult.reason === "resign"` — «انصراف» (session B; it was «خروج بدون بازگشت») at a
     *  table for two. */
    endOpponentLeft: "حریفت انصراف داد",
    endYouLeft: "میز رو ترک کردی",
    /**
     * `GameResult.reason === "idle"` — the TABLE removed a seat whose turns kept lapsing. At a
     * PUBLIC table for two that now ENDS the match (2026-09-21) instead of handing the chair to a
     * bot, so «حریفت میز رو ترک کرد» would be false on both screens: nobody left.
     *
     * ⚠️ IT SAYS WHAT HAPPENED, NOT WHAT IT COST THE OTHER PLAYER — the standing rule (operator,
     * 2026-09-15). «چند نوبت بازی نکرد» is a fact about the table, the same shape as
     * `online.idleReplacedByBot`; what the removal cost THEM is never this card's business.
     */
    endOpponentIdle: "حریفت چند نوبت پشت‌سرهم بازی نکرد",
    endYouIdle: "چند نوبت پشت‌سرهم بازی نکردی",
    playAgain: "بازی دوباره",
    // The tally that sits on each player's own chip — «{n} دست برده». The score used to be one
    // detached «۲ – ۱ از ۳» in a corner, viewer-relative and repeating the hand counter's own
    // «از ۳»; a number on the player who won it needs no legend.
    handsWon: "{n} دست برده",
    boardLabel: "زمین بازی",
    columnLabel: "ستون {col}",
    columnFull: "این ستون پره",
  },
  /**
   * «مار» (Snake) — the single-player game played wholly on the phone (operator, 2026-11-02): its
   * lobby tile, start screen, play screen and game-over card. The labels the operator named are his
   * words: «شروع», «دوباره», «بازگشت», «رکورد جدید!», آهسته / معمولی / سریع.
   */
  snake: {
    title: "مار",
    leave: "خروج",
    /** The lobby tile's chip, where the other games show their player count. */
    solo: "تک‌نفره",
    best: "رکورد تو",
    speed: "سرعت",
    speeds: { slow: "آهسته", normal: "معمولی", fast: "سریع" },
    look: "ظاهر",
    looks: { modern: "امروزی", retro: "نوستالژی" },
    start: "شروع",
    score: "امتیاز",
    record: "رکورد",
    paused: "مکث",
    /** The bonus's countdown chip, beside the title. */
    bonus: "جایزه",
    boardLabel: "زمین مار",
    pad: { up: "بالا", down: "پایین", left: "چپ", right: "راست" },
    gameOver: "بازی تموم شد",
    /** The snake filled the whole board — the one way a game of «مار» is won outright: the win card's
     *  title (the operator, 2026-11-02). Marked at BOTH ends — a leading mark alone does not keep a
     *  «!» in place (CLAUDE.md, «A LEADING RTL MARK DOES NOT FIX A TRAILING «!»»). */
    won: rtlLine("بردی! 🎉 کلِ صفحه رو پر کردی"),
    newRecord: "رکورد جدید!",
    again: "دوباره",
    back: "بازگشت",
  },
  sudoku: {
    title: "سودوکو",
    leave: "خروج",
    /** The lobby tile's chip, where the other games show their player count — as «مار»'s. */
    solo: "تک‌نفره",
    continueTitle: "بازی نیمه‌تمام",
    continueAction: "ادامه",
    newTitle: "بازی جدید",
    size: "اندازه",
    sizes: { 6: "۶×۶", 9: "۹×۹" },
    level: "سطح",
    levels: { easy: "آسان", medium: "متوسط", hard: "سخت" },
    start: "شروع",
    /** «شروع» after a start that got no answer (the network, a 5xx) — tapping it tries again. */
    startFailed: "شروع نشد؛ دوباره بزن",
    daily: "سودوکوی روز",
    dailyStart: "بازی کن",
    dailyContinue: "ادامه",
    /** The daily card once today's is solved: ✓ and the time. */
    dailySolved: "حل شد",
    /** The daily card after its start got no answer (beside its size and level; its badge says
     *  `tryAgain`). */
    dailyFailed: "شروع نشد",
    tryAgain: "دوباره",
    /** The start card's record line when the chosen size and level have none yet. */
    noRecordYet: "هنوز رکوردی نداری",
    /** The record line before the records are read (the read still out, or failed): says nothing. */
    recordUnknown: "—",
    /** «بازی جدید» while a game is unfinished: the web asks before the server replaces it. */
    replaceTitle: "بازی قبلی تموم نشده",
    replaceBody: rtlLine("با شروع بازی جدید، بازی نیمه‌تمام از دست می‌ره."),
    replaceConfirm: "بازی جدید",
    replaceCancel: "انصراف",
    notes: "یادداشت",
    erase: "پاک کن",
    undo: "برگرد",
    hint: "راهنمایی",
    boardLabel: "جدول سودوکو",
    paused: "مکث",
    solved: rtlLine("حلش کردی! 🎉"),
    time: "زمان",
    record: "رکورد",
    newRecord: "رکورد جدید!",
    again: "بازی جدید",
    back: "بازگشت",
    /** Another phone moved this game on: this one now shows the game as the server has it. */
    adopted: rtlLine("این بازی روی دستگاه دیگری جلو رفته بود؛ از همان‌جا ادامه بده."),
    /** Another phone started a NEW game in this slot: this one now shows that game. */
    replaced: rtlLine("روی دستگاه دیگری بازی جدیدی شروع شد؛ حالا همان را بازی می‌کنی."),
    /** The game this phone was playing was replaced or finished elsewhere. */
    gone: rtlLine("این بازی روی دستگاه دیگری تموم یا عوض شده."),
    loadFailed: "بارگیری نشد",
    retry: "دوباره امتحان کن",
    // ── Mistake checking (the operator, 2026-11-04) ──
    /** The «بازی جدید» card's switch — it applies to the NEXT game (each game keeps its own). */
    check: "بررسی اشتباه",
    /** The tools row's counter, with checking on: «n/۳» over this word. */
    mistakes: "اشتباه",
    mistakesLabel: (n: number, max: number): string => `اشتباه ${faDigits(n)} از ${faDigits(max)}`,
    /** The 3rd mistake: the game is over (the finish card's look). */
    lost: "بازی تمام شد",
    lostTitle: "۳ اشتباه — بازی تمام شد",
    /** …a daily: over for today. */
    lostDaily: rtlLine("سودوکوی امروز تمام شد؛ فردا یک جدول تازه."),
    /** The daily card once today's was lost: over for today, a new puzzle tomorrow. */
    dailyLost: "✗ با ۳ اشتباه تمام شد",
    dailyTomorrow: "فردا یک جدول تازه",
    /** Checking off, the first full grid with mistakes: how many, never where. */
    fullTitle: "جدول پر شد",
    fullWrong: (n: number): string => rtlLine(`جدول پر شد ولی ${faDigits(n)} خانه اشتباه است.`),
    /** Marks the wrong cells red — once per game, from the first full grid with mistakes. */
    showWhere: "نشانم بده",
    keepGoing: "ادامه",
  },
  mensch: {
    title: "منچ",
    // TWO levels, not three — منچ cannot support a third a player could tell apart (80.3% of its
    // turns offer no choice at all). Named for what they ARE rather than «متوسط»/«سخت», so
    // nothing implies a missing «آسان» rung below them.
    difficultyNormal: "حریف معمولی",
    difficultyHard: "حریف حرفه‌ای",
    roll: "تاس بریز",
    you: "شما",
    opponent: "حریف",
    leave: "خروج",
    token: "مهره",
    yard: "خانه",
    home: "خانهٔ امن",
    connecting: "در حال اتصال…",
    connectionError: "خطا در اتصال به بازی",
    gameOver: "پایان بازی",
    youWin: "بردی!",
    youLose: "باختی!",
    playAgain: "بازی دوباره",
    winSubtitle: "همهٔ مهره‌هات رسیدن خونه! 🎉",
    loseSubtitle: "این دفعه نشد — دوباره تلاش کن!",
    /** `GameResult.reason === "resign"` — the opponent's «انصراف» at a PRIVATE table for two
     *  (session B item 4). The card read no reason at all and greeted a forfeit with
     *  {@link winSubtitle}; a table fact, the same words شطرنج / تخته نرد / دوز now use. */
    endResign: "حریفت انصراف داد",
    // Contextual hint under the board — the player must always know what to do.
    turnOpponent: "نوبت حریف…",
    hintRoll: "نوبت توئه — «تاس بریز» رو بزن 🎲",
    hintMove: "با {die} حرکت کن — یک مهرهٔ روشن رو بزن",
    hintNoMove: "حرکتی نداری — نوبت رد می‌شه",
    hintReroll: "۶ آوردی ولی حرکتی نیست — دوباره «تاس بریز» 🎲",
    hintNeedSix: "برای بیرون آوردن مهره باید ۶ بیاری",
    bonusRoll: "۶ آوردی — یک تاس دیگه! 🎲",
    captured: "مهرهٔ حریف رو زدی!",
    gotCaptured: "مهره‌ات زده شد!",
    finishedTokens: "رسیده",
    color: "رنگ تو",
    chooseColor: "رنگت رو انتخاب کن",
    colorNames: ["طلایی", "آبی", "قرمز", "سبز"] as const,
    difficulty: "سطح حریف",
    chooseDifficulty: "سطح حریفت رو انتخاب کن",
    startGame: "شروع بازی",
    // Table size — the player picks it, offline AND online.
    players: "تعداد بازیکن‌ها",
    // Capture announcements — their OWN line, never mixed with the live turn status.
    captureOnYou: "{name} مهرهٔ شما را زد!",
    captureByYou: "مهرهٔ {name} رو زدی!",
    captureOther: "{by} مهرهٔ {of} را زد",
    yourRoll: "تاس تو",
    theirRoll: "تاس حریف",
    /**
     * THE PRIVATE TABLE's «تنظیمات میز» HINT — and now the only one.
     *
     * It used to read «تعداد صندلی‌های میز — بقیه با ربات پر می‌شن», which said the wrong thing
     * twice over. «بقیه» reads as «the seats BEYOND the ones you picked», so a «۲ نفره» pick
     * looked as though it left chairs over; and a PRIVATE table has no fill countdown at all —
     * nothing is bot-filled automatically there, a bot only ever appears if the host presses
     * «با ربات‌ها شروع کن».
     *
     * ⚠️ THE PUBLIC COUNTERPART IS GONE (2026-08-26). A public table offers no choices: every
     * quick-match منچ table is the standard four-handed one, so there is no step to hint at.
     */
    playersHint: "میز با همین تعداد صندلی ساخته می‌شود.",
    players2: "۲ نفره",
    players3: "۳ نفره",
    players4: "۴ نفره",
    // Online mid-game disconnect overlays (mirrors fa.backgammon wording).
    opponentReconnecting: "حریف قطع شد؛ منتظر اتصال دوباره…",
    opponentPaused: "بازی متوقف شد؛ منتظر حریف…",
    opponentLeft: "حریف از میز رفت",
    waitingReturn: "منتظر برگشت حریف…",
    claimHint: "می‌تونی برنده رو ادعا کنی یا منتظر برگشتش بمونی",
  },
  /**
   * مار پله (card-86). The operator's rules, word for word wherever a line states one: a roll past ۱۰۰
   * does NOT move the piece (no bounce anywhere), only exactly ۱۰۰ wins, and EVERY ۶ earns one more roll
   * — a ۶ that cannot move included. Lines that END in «!», «…» or an emoji are `rtlLine`-marked.
   */
  ladders: {
    title: "مار پله",
    leave: "خروج",
    roll: "تاس بریز",
    you: "شما",
    opponent: "حریف",
    connecting: "در حال اتصال…",
    connectionError: "خطا در اتصال به بازی",
    startGame: "شروع بازی",
    /** The size row (solo and the private host's). */
    players: "تعداد بازیکن‌ها",
    playersOption: (n: string): string => `${n} نفره`,
    /** The private table's «تنظیمات میز» hint — the منچ wording: a private table fills no seat itself. */
    playersHint: "میز با همین تعداد صندلی ساخته می‌شود.",
    /** ROUND 2 — the LEVEL row (solo and the private host's): a set of maps. Its three labels are the
     *  app's own «آسان» / «متوسط» / «سخت» (`fa.difficulty`) — the operator's words for the levels. */
    level: "سطح بازی",
    /** ROUND 2 — the LOOK row (solo and the private host's) and each look's name. The pickers offer
     *  only `LADDERS_LIVE_STYLES`. bg2 is named for what its picture shows: a Persian garden at
     *  dusk — cypresses, pomegranate trees, a pool and a golden pavilion. */
    look: "ظاهر صفحه",
    styleNames: { jungle: "جنگل", bg2: "باغ ایرانی", classic: "اورجینال" },
    gameOver: "پایان بازی",
    youWin: rtlLine("بردی!"),
    youLose: rtlLine("باختی!"),
    playAgain: "بازی دوباره",
    /** The board ended it: the winner stood exactly on ۱۰۰ — said ONLY then (a forfeit is no ۱۰۰). */
    winSubtitle: rtlLine("رسیدی به ۱۰۰! 🎉"),
    loseSubtitle: (name: string): string => rtlLine(`${isolate(name)} زودتر به ۱۰۰ رسید`),
    /** The ROOM ended it at a table for two (`GameResult.reason`): a «خروج» or an idle removal —
     *  each says what happened at the table or to the reader, never what it cost anybody else. */
    endOpponentLeft: "حریفت بازی رو ترک کرد",
    endYouLeft: "تو بازی رو ترک کردی",
    endOpponentIdle: "حریفت چند نوبت پشت‌سرهم بازی نکرد",
    endYouIdle: "چند نوبت پشت‌سرهم بازی نکردی",
    /** The hint line under the board: what to do now, or what the last throw or move did. ROUND 2's
     *  turn is two taps — the die, then your piece. */
    hintRoll: "روی تاس بزن",
    hintMove: "روی مهره‌ات بزن",
    hintSixAgain: rtlLine("۶ آوردی! دوباره بریز"),
    hintExact: (n: string): string => `باید دقیقاً ${n} بیاری`,
    hintLadder: rtlLine("از پله رفتی بالا ⬆️"),
    hintSnake: rtlLine("مار نیشت زد! اومدی پایین ⬇️"),
    /** A HIT (round 2's capture) — who hit whom, every name a first-strong isolate (a Latin name never
     *  turns the line around). منچ's three shapes: you hit, you were hit, two others. */
    captureByYou: (name: string): string => rtlLine(`مهرهٔ ${isolate(name)} رو زدی!`),
    captureOnYou: (name: string): string => rtlLine(`${isolate(name)} مهرهٔ شما را زد!`),
    captureOther: (by: string, of: string): string =>
      rtlLine(`${isolate(by)} مهرهٔ ${isolate(of)} را زد`),
    turnOf: (name: string): string => rtlLine(`نوبت ${isolate(name)}…`),
    /** The seat strip's figure: the cell a seat stands on — «شروع» before its first roll. */
    atStart: "شروع",
    cellLabel: (n: string): string => `خانهٔ ${n}`,
    /** What a screen reader says for your movable piece (the board's tap target) — as `roll` for the die. */
    movePiece: "حرکت مهره",
  },
  backgammon: {
    // ITEM 12 — the checker colour the player takes. Deliberately NOT «رنگ مهره‌ها» (chess's
    // label): this one is viewer-side and about YOUR OWN checkers, and two identical strings
    // made the two settings screens indistinguishable in a query.
    color: "رنگ مهره‌های تو",
    colorGold: "طلایی",
    colorObsidian: "مشکی",
    /** ⚠️ ONE LINE, MEASURED: 140px against 256px on a 320px phone. The second clause («حریفت
     *  مهره‌هایش را با رنگ خودش می‌بیند») pushed it to 348px = two lines, and it only restated the
     *  first: «فقط برای صفحهٔ خودت» already says the opponent is unaffected. */
    colorHint: "فقط برای صفحهٔ خودت است.",
    // HOST-facing ONLINE table settings (میز خصوصی). Only the choices the server actually
    // ENFORCES are offered: the PER-TURN window, the MATCH LENGTH (passed to the engine as
    // `matchTo`) and what happens on a disconnect. `side` and the doubling cube are still in the
    // schema with no effect in v1, and a control that changes nothing is worse than no control.
    // ⚠️ THE WHOLE-GAME CLOCK IS GONE (operator, 2026-08-15) — تخته نرد runs exactly ONE clock,
    // the per-turn window below, which is also what the idle handover counts.
    turnTimerHint: "اگر پنج نوبت پشت‌سرهم بازی نکنی، ربات جایت را می‌گیرد.",
    /**
     * The header scoreboard's caption — which دست, out of how many. Digits arrive in Persian.
     *
     * ⚠️ «دست ۱ · تا ۵» was the old form and it read as a stray dot plus an unqualified number; a
     * player could not tell what «تا ۵» counted. «از» is the ordinary Persian «out of».
     */
    scoreCaption: (round: string, target: string): string => `دست ${round} از ${target}`,
    /**
     * …and the form used once the دست number PASSES the target. A مارس pays 2 دست and a سگ‌مارس 3,
     * so a match to five is at MOST five دست but usually fewer — yet alternating single wins can
     * legitimately run a «تا ۵» match to nine دست, and «دست ۶ از ۵» is exactly the nonsense being
     * fixed. Past the target the caption simply states which دست this is.
     */
    scoreCaptionOver: (round: string): string => `دست ${round}`,
    scoreAria: (mine: string, theirs: string, round: string, target: string): string =>
      `امتیاز تو ${mine}، حریف ${theirs} — دست ${round}، مسابقه تا ${target} دست`,
    disconnect: "قطع اتصال حریف",
    dcClaim: "ادعای برد",
    dcBot: "ربات",
    dcPause: "توقف",
    title: "تخته نرد",
    roll: "تاس بریز",
    double: "دوبل",
    take: "قبول",
    drop: "رد",
    undo: "برگرداندن",
    confirm: "تأیید حرکت",
    confirmShort: "تأیید",
    bearOff: "بیرون بردن",
    hit: "زده شد",
    bar: "بار",
    point: "خانه",
    /** «دست ۲» — which دست of the match is on the table. Absent in a single دست. */
    roundNo: (n: string) => `دست ${n}`,
    /** «۶ (۴ بار)» — a double is ONE number played several times, never «۶ و ۶ و ۶ و ۶». */
    diceTimes: (die: string, times: string) => `${die} (${times} بار)`,
    /** A die still tumbling. Its VALUE must not be announced before it lands. */
    dieRolling: "تاس در حال چرخش",
    /** «+۱۰ ثانیه» — the extra time a DOUBLE stretches the turn by. The number is derived from the
     *  table's own window (1.5× − 1×), so a 30s private table reads «+۱۵ ثانیه». */
    timeGranted: (sec: string) => `+${sec} ثانیه`,
    mars: "مارس",
    sagMars: "سگ‌مارس",
    /** The round-result banner between دست‌ها, and the match score on each panel. */
    roundWonYou: "این دست را بردی",
    roundWonOpp: "این دست را حریف برد",
    roundPlain: "۱ امتیاز",
    roundMars: "مارس — ۲ امتیاز",
    roundSagMars: "سگ‌مارس — ۳ امتیاز",
    nextRound: "دست بعد",
    /** «۳ از ۵» — points won, out of the match target. */
    matchScore: (won: string, target: string) => `${won} از ${target}`,
    matchLength: "طول مسابقه",
    /** ⚠️ ONE LINE, and it is MEASURED: 244px against the 256px a Card gives it on a 320px phone
     *  (12px Vazirmatn, the `text-caption` scale). The old wording opened with «مسابقه تا این تعداد
     *  دست» and ran to 385px — it wrapped to two lines on every phone. The picker above it already
     *  says what the number means, so the sentence only has to carry what it does NOT say. */
    matchLengthHint: "مارس ۲ دست و سگ‌مارس ۳ دست حساب می‌شود.",
    /**
     * «۵ دست» — the colloquial Iranian phrasing, and it is EXACT rather than loose: the scoring
     * unit of a تخته نرد match IS the دست, and a مارس simply counts as two of them (سگ‌مارس three).
     * So «۵ دست» and "first to five points" are the same sentence. `matchSingle` is gone: «۱ دست»
     * belongs in the same grammar as the other three, and the odd one out was what made the picker
     * read as a list of unrelated things.
     */
    matchLengthOption: (n: string) => `${n} دست`,
    noMove: "پاس — حرکتی نداری",
    // Contextual hint line under the board (mirrors the design's «حرکت با ۵ و ۶ — مهره را بکشید»).
    turnOpponent: "نوبت حریف…",
    hintRoll: "نوبت توئه — «تاس بریز» رو بزن 🎲",
    hintBar: "مهره‌ات روی بار است — واردش کن",
    hintNoMove: "حرکتی نداری — «تأیید» رو بزن",
    hintConfirm: "تمام شد — «تأیید حرکت» رو بزن ✓",
    hintMove: "حرکت با {dice} — یک مهرهٔ روشن رو بزن",
    /* §5.1 — the opening contest, thrown by the players themselves (M1, 2026-08-14). */
    /* SHORT ON PURPOSE. This message shares the midfield band with BOTH contest dice, so every
       character it costs is clearance the dice lose — «برای شروع، » alone was ~55px of the ~150px
       the card measured, and the gap to each die came out at 8–10px. */
    contestThrow: "تاست رو بنداز 🎲",
    contestWaiting: "منتظر تاس حریف…",
    contestTie: "مساوی شد — دوباره بندازید",
    // §5.1 — the opening roll-off: each side throws ONE die, the higher starts, the winner re-rolls.
    openingYou: "شروع با تو",
    openingOpp: "شروع با حریف",
    // §5.2 — a checker that hit inside your OWN home board is locked for the rest of this turn. The
    // engine just stops offering it, which on screen read as the board refusing to move a checker.
    /*
     * §5.2 — and this band toast has a HARD, MEASURED CEILING of about 31 characters. Do not lengthen
     * it without re-measuring; it clips SILENTLY (`overflow: hidden`).
     *
     * The toast lives in the band's MESSAGE HALF — `width: 44%` of the felt, so 71–141px wide — and
     * the band itself is only 46–158px tall. Measured in Chromium over 11 viewports: 41 characters
     * wraps to three lines and clips on every phone at or under 375px wide; 31 fits everywhere from
     * 320×568 up, in two lines of the tight 12px step. The old «…زد، همین نوبت قفل است» (45) and its
     * replacement carrying an advice clause (89) were BOTH clipped — the second on 6 of 11 viewports,
     * losing 5 of its 10 lines at 320×480 — which is how the ceiling came to be measured at all.
     *
     * WHAT SURVIVED THE BUDGET, and why these two: «نشان‌دار» is WHICH checker (the board rings it
     * with `.bg-frozen`), «تا نوبت بعد» is HOW LONG. The «what to do instead» clause does not fit in
     * any form and was dropped rather than shipped clipped — pinned by `frozen-hint-fits.test.ts`.
     */
    frozenHint: "مهرهٔ نشان‌دار تا نوبت بعد قفله",
    /**
     * §5.2 EXCEPTION 2, and the ONLY exception a player can watch happen: they hit inside their own
     * home and the checker moves anyway, because nothing else can. Never shown otherwise — a double
     * (exception 1) withholds nothing, so there is nothing to explain there. Same 31-char ceiling.
     */
    frozenLiftedHint: "چون حرکت دیگه نداری، آزاده",
    // §5.4 (maximal play) — only one of the two dice can be played, so «تمام شد» would be wrong.
    oneDieOnly: "فقط یک تاس قابل بازی است — «تأیید» رو بزن",
    // The turn, stated in words on the active player's panel (the board's primary "whose move is
    // it" signal — a ring alone never told anyone whose ring it was).
    turnYours: "نوبت شماست",
    turnTheirs: "نوبت حریف",
    // The midfield band's toasts.
    notYourTurn: "نوبت تو نیست",
    // card-98 item 6: shown ALONE on a turn with no move (closed out / a danced roll), which the
    // server then passes on its own — the old «تأیید خودکار…» countdown under it is gone.
    blocked: "حرکتی نداری",
    // Per-turn timer strikes. FIVE expiries and the bot takes the seat — permanently (منچ's rule;
    // see `abandonToBot`). The «دو نوبت» these used to say predates the 45s→15s window change.
    strikes: "اخطار",
    strikeGot: "یک نوبت را از دست دادی",
    seatToBot: "پنج نوبت را از دست دادی — ربات جای تو بازی می‌کند",
    seatToBotOpp: "حریف پنج نوبت را از دست داد — ربات جایگزینش شد",
    you: "شما",
    opponent: "حریف",
    leave: "خروج",
    connecting: "در حال اتصال…",
    connectionError: "خطا در اتصال به بازی",
    gameOver: "پایان بازی",
    youWin: "بردی!",
    youLose: "باختی!",
    marsWin: "مارس بردی! 🎲",
    marsLose: "مارس شدی!",
    // سگ‌مارس — the THIRD tier, worth 3 points. It is ON by default (`countSagMars`), so the
    // end card must never call a 3 a «مارس». Named سگ‌مارس, never «ترکی» — that is not what the
    // Iranian game calls it.
    sagMarsWin: "سگ‌مارس بردی! 🏆",
    sagMarsLose: "سگ‌مارس شدی!",
    tripleStakes: "سه برابر",
    /**
     * The end card of a MULTI-دست match: the headline states the match, this states its score.
     *
     * ⚠️ THE TWO NUMBERS ARE LABELLED (operator, 2026-08-15). «نتیجهٔ مسابقه: ۳ — ۱» left the player
     * to guess which half was theirs — and there is no seat, colour or position on this card to
     * infer it from. Same «شما … حریف» shape the board's own scoreboard uses, so the card and the
     * felt read the same way round.
     */
    matchFinal: (mine: string, theirs: string) => `نتیجهٔ مسابقه: شما ${mine} — ${theirs} حریف`,
    /**
     * WHY the match ended when the BOARD did not — a forfeit or a claim leaves `view.status` at
     * «playing», so the card fell back to «یک بازی عالی داشتی!», which is not what happened: the
     * opponent walked out. Mirrors `fa.chess.endResign`/`endClaim`, which already do this.
     * «resign» is the two-seat «انصراف» (session B item 4), named the way the button names it.
     */
    endResign: "حریفت انصراف داد؛ برد به نام تو ثبت شد",
    endClaim: "چون حریفت برنگشت، نتیجه به نفع تو ثبت شد",
    endResignLost: "تو بازی رو ترک کردی",
    endClaimLost: "چون برنگشتی، نتیجه به نفع حریفت ثبت شد",
    /**
     * `GameResult.reason === "idle"` — the TABLE removed a seat whose turns kept lapsing, which
     * at a PUBLIC table for two now ENDS the match (2026-09-21). تخته نرد removes at THREE misses
     * rather than five (`BACKGAMMON_IDLE_MISS_LIMIT`), so this is the likeliest card on the
     * platform to carry it. Nobody left, so the resign copy above would be false on both screens.
     */
    endIdle: "حریفت چند نوبت پشت‌سرهم بازی نکرد؛ برد به نام تو ثبت شد",
    endIdleLost: "چند نوبت پشت‌سرهم بازی نکردی",
    doubleStakes: "دو برابر",
    playAgain: "بازی دوباره",
    // Solo difficulty — chosen on the START screen, LOCKED for the game (backgammon-specific).
    difficulty: "سطح حریف",
    chooseDifficulty: "سطح حریفت رو انتخاب کن",
    startGame: "شروع بازی",
    difficultyRestart: "با تغییر دشواری، بازی از نو شروع می‌شود.",
    difficultyStart: "شروع دوباره",
    // End-overlay subtitles (no coins/wallet — that's not a v1 feature).
    winSubtitle: "یک بازی عالی داشتی!",
    loseSubtitle: "این دفعه نشد — دوباره تلاش کن!",
    // Online mid-game disconnect overlays (the OpponentAwayOverlay — mirrors fa.chess wording).
    opponentReconnecting: "حریف قطع شد؛ منتظر اتصال دوباره…",
    opponentPaused: "بازی متوقف شد؛ منتظر حریف…",
    opponentLeft: "حریف از میز رفت",
    waitingReturn: "منتظر برگشت حریف…",
    claimHint: "می‌تونی برنده رو ادعا کنی یا منتظر برگشتش بمونی",
  },
} as const;
